import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ApplyLoanPage.css';
import { useBrand } from '../context/BrandContext';
import { useFeatureFlags } from '../context/FeatureFlagsContext';

const API = process.env.REACT_APP_API_URL || '';

// 7-Stage Journey (Clean & Professional)
const STEPS = [
    { id: 0, key: 'employment', title: 'Employment Info', icon: '💼', color: '#0891b2', desc: 'Tell us about your income sources.' },
    { id: 1, key: 'digilocker', title: 'Digilocker KYC', icon: '📜', color: '#ea580c', desc: 'Complete Aadhaar verification on the dedicated KYC page.' },
    { id: 2, key: 'aa', title: 'Account Aggregator', icon: '🏦', color: '#d97706', desc: 'Securely link bank statements.' },
    { id: 3, key: 'bank', title: 'Bank Verification', icon: '💳', color: '#a5761e', desc: 'Penny drop verification for disbursal account.' },
    { id: 4, key: 'selfie', title: 'Selfie Verification', icon: '📸', color: '#e11d48', desc: 'Complete liveness and face match with Aadhaar in one quick step.' },
    { id: 5, key: 'declaration', title: 'Declaration', icon: '🎥', color: '#a5761e', desc: 'Record a short video reading your loan declaration aloud.' },
    { id: 6, key: 'address_proof', title: 'Address Proof', icon: '🏠', color: '#0284c7', desc: 'Upload address proof for location verification.' },
    { id: 7, key: 'review', title: 'Submit', icon: '🚀', color: '#10b981', desc: 'Final review and instant disbursal.' },
];

// Maps each STEPS index to the flag that gates it (null = always enabled).
// Keep in sync with the STEPS array above.
const STEP_FLAG_KEYS = [null, 'kycStep', 'aaStep', 'bankStep', 'selfieStep', 'videoDeclarationStep', 'addressProofStep', null];

// Shown in place of a step's real UI when its feature flag is switched off.
function StepDisabledNotice({ title }) {
    return (
        <div className="alp-step-body">
            <div className="alp-bank-panel" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', background: '#f8fafc', borderColor: '#e2e8f0', minHeight: '300px' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏭️</div>
                <h3 style={{ color: '#334155', margin: '0 0 0.5rem 0' }}>{title} Skipped</h3>
                <p style={{ color: '#64748b', margin: 0, fontSize: '0.95rem', maxWidth: '400px' }}>
                    This step is temporarily disabled. You can continue to the next step.
                </p>
            </div>
        </div>
    );
}

const EMP_TYPES = ['Salaried', 'Self-Employed'];
const EMPLOYMENT_OPTIONS = {
    'Salaried': {
        icon: '💼',
        tone: 'cyan',
        label: 'Salaried',
        desc: 'Fixed monthly salary from a company'
    },
    'Self-Employed': {
        icon: '🛠️',
        tone: 'green',
        label: 'Self-Employed',
        desc: 'Business, freelance or consulting income'
    }
};

const EMPLOYMENT_FIELDS = {
    'Salaried': [
        { key: 'employer', label: 'Company Name', placeholder: 'e.g. Tata Consultancy Services', icon: '🏢' },
        { key: 'designation', label: 'Designation', placeholder: 'e.g. Senior Manager', icon: '👔' },
        { key: 'income', label: 'Monthly Net Salary (₹)', placeholder: '75000', type: 'number', icon: '💰' },
        { key: 'salary_date', label: 'Salary Date (1-31)', placeholder: 'e.g. 5', type: 'number', icon: '📅' },
        { key: 'epfo_number', label: 'EPFO Number (Optional)', placeholder: 'e.g. MHBAN00000000000000000', type: 'text', icon: '🏛️', required: false },
        { key: 'personal_email', label: 'Personal Email Address', placeholder: 'e.g. you@gmail.com', type: 'email', icon: '✉️' },
        { key: 'email', label: 'Company Email Address', placeholder: 'e.g. you@company.com', type: 'email', icon: '📧', required: false }
    ],
    'Self-Employed': [
        { key: 'employer', label: 'Business Name', placeholder: 'e.g. Blue Peak Traders', icon: '🏪' },
        { key: 'businessType', label: 'Profession/Trade', placeholder: 'e.g. Consultant, Architect', icon: '🛠️' },
        { key: 'income', label: 'Monthly Income (₹)', placeholder: '80000', type: 'number', icon: '💰' },
        { key: 'salary_date', label: 'Income Receipt Date (1-31)', placeholder: 'e.g. 10', type: 'number', icon: '📅', required: false },
        { key: 'epfo_number', label: 'EPFO Number (Optional)', placeholder: 'e.g. MHBAN00000000000000000', type: 'text', icon: '🏛️', required: false },
        { key: 'personal_email', label: 'Personal Email Address', placeholder: 'e.g. you@gmail.com', type: 'email', icon: '✉️' },
        { key: 'email', label: 'Company Email Address', placeholder: 'e.g. you@company.com', type: 'email', icon: '📧', required: false }
    ]
};

const REFERENCE_RELATIONS = [
    'Father', 'Mother', 'Spouse', 'Brother', 'Sister',
    'Son', 'Daughter', 'Friend', 'Colleague', 'Relative', 'Other'
];

const REFERENCE_SLOTS = [
    { n: 1, namePlaceholder: 'e.g. REFE1', phonePlaceholder: 'e.g. 9876543210' },
    { n: 2, namePlaceholder: 'e.g. REFE2', phonePlaceholder: 'e.g. 9876543210' }
];

// Free-text relation, only required when 'Other' is picked.
const CUSTOM_RELATION_RE = /^[A-Za-z][A-Za-z\s.'-]{1,49}$/;

const INDIAN_MOBILE_RE = /^[6-9]\d{9}$/;

// Strips +91 / 0 prefixes so a stored "+919876543210" compares equal to "9876543210".
function toIndianMobile(value) {
    let digits = String(value || '').replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
    if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
    return digits;
}

// Which loan statuses mean "you already have an application with us" and so
// keep the borrower on the submitted screen instead of the application form.
//
// This used to be a list of the statuses that BLOCK — 'pending', 'approved',
// 'active' — and missed every stage the admin pipeline moves a case through
// after Credit Exec picks it up (PENDING_CREDIT_EXEC, PENDING_CREDIT_HEAD,
// PENDING_DEEP_REVIEW, APPROVAL_NEEDED_FOR_DISBURSAL, APPROVED_FOR_DISBURSAL).
// That is what actually produced the duplicate applications: once a case was
// approved out of the first queue this returned false, the hydrate effect below
// took the "old non-blocking loan + completed draft" path, and the app WIPED
// the borrower's journey and handed them a blank application form — so they
// filled it in again in good faith and a second loan was created.
//
// Inverted onto the terminal statuses: only a Closed, Completed or Rejected loan frees
// the borrower to apply again, everything else keeps them on the submitted
// screen. A stage added to the pipeline later blocks by default rather than
// silently reopening this. An unrecognised or empty status counts as
// in-flight too — the safe direction, since the worst case is showing the
// submitted screen to someone who could have applied, rather than creating a
// duplicate loan.
const TERMINAL_LOAN_STATUSES = new Set(['closed', 'completed', 'rejected']);

function isBlockingLoanStatus(status) {
    return !TERMINAL_LOAN_STATUSES.has(String(status || '').trim().toLowerCase());
}

function getLoanBreakdown(amount, days) {
    const amt = Number(amount || 50000);
    const d = parseInt(days || 14);
    const processingFee = amt * 0.10;
    const gstOnFee = processingFee * 0.18;
    const netDisbursal = amt - processingFee - gstOnFee;
    // Interest is charged on both the disbursal day and the due day, so a
    // d-day tenure earns d + 1 days — matches POST /api/loans/apply.
    const totalInterest = amt * (0.01 * (d + 1));
    const totalRepayment = Math.round(amt + totalInterest);
    return { amount: amt, days: d, processingFee, gstOnFee, netDisbursal, totalInterest, totalRepayment };
}

const DECLARATION_MIN_SECONDS = 20;
const DECLARATION_MAX_SECONDS = 90;

// Browsers disagree on container/codec support; pick the first the runtime accepts.
function pickVideoMimeType() {
    if (typeof MediaRecorder === 'undefined') return '';
    const candidates = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm',
        'video/mp4'
    ];
    return candidates.find(type => MediaRecorder.isTypeSupported(type)) || '';
}

// Links opened from WhatsApp/Instagram/Facebook load in an in-app browser that
// often can't use the camera at all.
function isInAppBrowser() {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    return /WhatsApp|Instagram|FBAN|FBAV|FB_IAB|Line\/|; wv\)/i.test(ua);
}

// Tell the applicant what actually went wrong; the error name is appended so
// support can tell cases apart from a screenshot.
function describeRecordingError(err) {
    const name = err?.name || 'Error';
    let message;
    if (isInAppBrowser()) {
        message = 'This in-app browser cannot record video. Open this page in Chrome or Safari and try again.';
    } else if (name === 'NotAllowedError' || name === 'SecurityError') {
        message = 'Camera or microphone permission was blocked. Allow both for this site in your browser settings, then tap Start Recording again.';
    } else if (name === 'NotFoundError') {
        message = 'No camera or microphone was found on this device.';
    } else if (name === 'NotReadableError' || name === 'AbortError') {
        message = 'Your camera or microphone is being used by another app. Close other apps (calls, camera, video apps) and try again.';
    } else if (name === 'NoMediaDevices') {
        message = 'This browser cannot access the camera. Please use the latest Chrome or Safari.';
    } else {
        message = 'Could not start the recording. Please reload the page and try again, or use the latest Chrome or Safari.';
    }
    return `${message} (${name})`;
}

// The applicant reads these lines aloud on camera. Keep each line short enough
// to speak in one breath.
function buildDeclarationScript({ name, brandName, companyLegalName }) {
    const lender = brandName || 'FinMudra';
    const nbfc = companyLegalName || 'MLB Securities Pvt. Ltd.';
    const intro = name ? `Hi, my name is ${name}.` : 'Hi, I am the loan applicant.';

    return [
        intro,
        `I am availing a short-term personal loan from ${lender}.`,
        `I authorise the NBFC ${nbfc} to visit my home or house in case of default.`,
        `Thank you.`
    ];
}

function hexToRgb(hex) {
    const value = String(hex || '').replace('#', '').trim();
    if (!/^[0-9a-fA-F]{6}$/.test(value)) {
        return '37, 99, 235';
    }

    const int = parseInt(value, 16);
    const r = (int >> 16) & 255;
    const g = (int >> 8) & 255;
    const b = int & 255;
    return `${r}, ${g}, ${b}`;
}

function normalizeAccountNumber(value) {
    return String(value || '').replace(/\s+/g, '');
}

function normalizeIfscCode(value) {
    return String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
}

function StepDeclarationVideo({ data, onChange }) {
    const liveVideoRef = useRef(null);
    const playbackRef = useRef(null);
    const recorderRef = useRef(null);
    const recStreamRef = useRef(null);
    const chunksRef = useRef([]);

    const [recording, setRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [videoBlob, setVideoBlob] = useState(null);
    const [videoUrl, setVideoUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [videoError, setVideoError] = useState('');

    const session = JSON.parse(localStorage.getItem('userSession') || '{}');
    const userId = session?.userId;
    const token = session?.token || '';

    const { brandName, companyLegalName } = useBrand();
    const { videoDeclarationStep } = useFeatureFlags();
    const done = !!data.declarationVideoDone;
    const applicantName = data.applicantName || data.bankBeneficiaryName || session?.name || '';
    const script = buildDeclarationScript({
        name: applicantName,
        brandName,
        companyLegalName
    });

    useEffect(() => {
        if (!videoDeclarationStep && !done) onChange('declarationVideoDone', true);
    }, [videoDeclarationStep, done, onChange]);

    const stopRecStream = useCallback(() => {
        if (recStreamRef.current) {
            recStreamRef.current.getTracks().forEach(track => track.stop());
            recStreamRef.current = null;
        }
    }, []);

    const stopRecording = useCallback(() => {
        if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
        setRecording(false);
    }, []);

    // Tick the timer and hard-stop at the max duration.
    useEffect(() => {
        if (!recording) return undefined;
        const id = setInterval(() => {
            setElapsed(prev => {
                if (prev + 1 >= DECLARATION_MAX_SECONDS) stopRecording();
                return prev + 1;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [recording, stopRecording]);

    useEffect(() => () => {
        if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
        stopRecStream();
    }, [stopRecStream]);

    useEffect(() => () => {
        if (videoUrl) URL.revokeObjectURL(videoUrl);
    }, [videoUrl]);

    const startRecording = async () => {
        setVideoError('');
        if (typeof MediaRecorder === 'undefined') {
            setVideoError('Your browser cannot record video. Please use the latest Chrome, Edge or Safari.');
            return;
        }

        if (videoUrl) URL.revokeObjectURL(videoUrl);
        setVideoBlob(null);
        setVideoUrl('');
        setElapsed(0);

        try {
            if (!navigator.mediaDevices?.getUserMedia) {
                const err = new Error('getUserMedia unavailable');
                err.name = 'NoMediaDevices';
                throw err;
            }
            let stream;
            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 960 } },
                    audio: true
                });
            } catch (err) {
                // Some phones reject the size/facing hints or a busy front camera;
                // a plain request still gets a usable stream on most of them.
                if (err?.name !== 'OverconstrainedError' && err?.name !== 'NotReadableError') throw err;
                stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            }
            recStreamRef.current = stream;
            if (liveVideoRef.current) {
                liveVideoRef.current.srcObject = stream;
                // The preview is cosmetic; a rejected play() must not block recording.
                try { await liveVideoRef.current.play(); } catch { /* autoPlay retries it */ }
            }

            chunksRef.current = [];
            let mimeType = pickVideoMimeType();
            let recorder;
            try {
                recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            } catch {
                // isTypeSupported() can claim a codec the encoder then refuses.
                mimeType = '';
                recorder = new MediaRecorder(stream);
            }
            recorderRef.current = recorder;

            recorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
            };
            recorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mimeType || recorder.mimeType || 'video/webm' });
                setVideoBlob(blob);
                setVideoUrl(URL.createObjectURL(blob));
                stopRecStream();
            };

            recorder.start();
            setRecording(true);
        } catch (err) {
            setVideoError(describeRecordingError(err));
            stopRecStream();
        }
    };

    const uploadVideo = async () => {
        if (!videoBlob || !userId) return;
        setUploading(true);
        setVideoError('');

        const ext = videoBlob.type.includes('mp4') ? 'mp4' : 'webm';
        const fileName = `declaration_${userId}_${Date.now()}.${ext}`;
        const documentType = 'Declaration Video';

        try {
            const presignRes = await fetch(`${API}/api/documents/presigned-url`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({ fileName, fileType: videoBlob.type || 'video/webm', documentType, userId })
            });
            if (!presignRes.ok) {
                const err = await presignRes.json().catch(() => ({}));
                throw new Error(err.message || 'Could not start the upload.');
            }
            const { presignedUrl, s3Key } = await presignRes.json();

            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('PUT', presignedUrl);
                xhr.setRequestHeader('Content-Type', videoBlob.type || 'video/webm');
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) resolve();
                    else reject(new Error(`Upload failed (status ${xhr.status})`));
                };
                xhr.onerror = () => reject(new Error('Network error during upload.'));
                xhr.send(videoBlob);
            });

            const confirmRes = await fetch(`${API}/api/documents/confirm-upload`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({ s3Key, originalName: fileName, documentType, fileSize: videoBlob.size, userId })
            });
            const json = await confirmRes.json();
            if (!confirmRes.ok || !json.success) throw new Error(json.message || 'Upload failed.');

            onChange('declarationVideoDone', true);
            onChange('declarationVideoDocId', json.document?.id || null);
            onChange('declarationVideoRecordedAt', new Date().toISOString());
        } catch (err) {
            setVideoError(err.message || 'Could not upload the declaration video.');
        } finally {
            setUploading(false);
        }
    };

    const reRecord = () => {
        onChange('declarationVideoDone', false);
        onChange('declarationVideoDocId', null);
        if (videoUrl) URL.revokeObjectURL(videoUrl);
        setVideoBlob(null);
        setVideoUrl('');
        setElapsed(0);
        setVideoError('');
    };

    const tooShort = elapsed < DECLARATION_MIN_SECONDS;

    if (!videoDeclarationStep) return <StepDisabledNotice title="Video Declaration" />;

    return (
        <div className="alp-step-body">
            <div className="alp-decl">
                <div className="alp-decl-head">
                    <span className="alp-decl-kicker">Video Declaration</span>
                    <h3>Read this out on camera</h3>
                    <p>Face the camera, speak clearly, and read the lines below aloud. A minimum {DECLARATION_MIN_SECONDS}-second video is required.</p>
                </div>

                <div className="alp-decl-script">
                    {script.map((line, i) => (
                        <p key={i}><span className="alp-decl-line-no">{i + 1}</span>{line}</p>
                    ))}
                </div>

                {videoError && (
                    <div className="alp-alert alp-aa-error" style={{ marginTop: '0.9rem' }}>
                        <strong>Video</strong> {videoError}
                    </div>
                )}

                {done ? (
                    <div className="alp-decl-done">
                        <span className="alp-decl-done-icon">✅</span>
                        <div>
                            <strong>Declaration video recorded</strong>
                            <p>Saved with your application. You can continue.</p>
                        </div>
                        <button className="alp-btn-ghost" onClick={reRecord} disabled={uploading}>Re-record</button>
                    </div>
                ) : (
                    <>
                        <div className="alp-decl-stage">
                            {/* Live preview while recording, playback once stopped. */}
                            <video
                                ref={liveVideoRef}
                                playsInline
                                muted
                                autoPlay
                                style={{ display: videoUrl ? 'none' : 'block' }}
                            />
                            <video
                                ref={playbackRef}
                                src={videoUrl || undefined}
                                controls
                                playsInline
                                style={{ display: videoUrl ? 'block' : 'none' }}
                            />
                            {recording && (
                                <div className="alp-decl-timer">
                                    <span className="alp-decl-rec-dot" />
                                    {String(Math.floor(elapsed / 60)).padStart(2, '0')}:{String(elapsed % 60).padStart(2, '0')}
                                </div>
                            )}
                        </div>

                        <div className="alp-decl-actions">
                            {!recording && !videoUrl && (
                                <button className="alp-btn-primary" onClick={startRecording}>Start Recording</button>
                            )}
                            {recording && (
                                <button className="alp-btn-primary" onClick={stopRecording} disabled={tooShort}>
                                    {tooShort ? `Keep reading… ${DECLARATION_MIN_SECONDS - elapsed}s` : 'Stop Recording'}
                                </button>
                            )}
                            {!recording && videoUrl && (
                                <>
                                    <button className="alp-btn-primary" onClick={uploadVideo} disabled={uploading}>
                                        {uploading ? 'Uploading…' : 'Submit Video'}
                                    </button>
                                    <button className="alp-btn-ghost" onClick={startRecording} disabled={uploading}>Record Again</button>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function StepSelfieCapture({ data, onChange }) {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const previewFrameRef = useRef(null);
    // Guards against re-POSTing the same image on re-render / StrictMode double-mount.
    const selfieUploadRef = useRef('');
    const [cameraReady, setCameraReady] = useState(false);
    const [captured, setCaptured] = useState(data.selfieDone || false);
    const [preview, setPreview] = useState(data.selfieImage || '');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [livenessStatus, setLivenessStatus] = useState('idle');
    const [photoSaveError, setPhotoSaveError] = useState('');

    const session = JSON.parse(localStorage.getItem('userSession') || '{}');
    const userId = session?.userId;
    const token = session?.token || '';

    const { selfieStep } = useFeatureFlags();

    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
    }, []);

    const startCamera = useCallback(async () => {
        setError('');
        setLoading(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'user',
                    width: { ideal: 720 },
                    height: { ideal: 960 },
                    aspectRatio: { ideal: 0.75 }
                },
                audio: false
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }
            setCameraReady(true);
        } catch (err) {
            setError('Camera access is required to take a selfie.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (captured || !selfieStep) return undefined;
        startCamera();
        return () => stopCamera();
    }, [captured, selfieStep, startCamera, stopCamera]);

    useEffect(() => {
        if (!selfieStep && !data.selfieDone) onChange('selfieDone', true);
    }, [selfieStep, data.selfieDone, onChange]);

    const captureSelfie = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;

        const sourceWidth = video.videoWidth || 720;
        const sourceHeight = video.videoHeight || 720;
        const frameRect = previewFrameRef.current?.getBoundingClientRect();
        const frameAspectRatio = frameRect?.width && frameRect?.height
            ? frameRect.width / frameRect.height
            : sourceWidth / sourceHeight;

        let cropWidth = sourceWidth;
        let cropHeight = sourceHeight;
        let cropX = 0;
        let cropY = 0;

        // Match the saved selfie to the same framing shown in the live preview.
        if (sourceWidth / sourceHeight > frameAspectRatio) {
            cropWidth = sourceHeight * frameAspectRatio;
            cropX = (sourceWidth - cropWidth) / 2;
        } else {
            cropHeight = sourceWidth / frameAspectRatio;
            cropY = (sourceHeight - cropHeight) / 2;
        }

        canvas.width = Math.round(cropWidth);
        canvas.height = Math.round(cropHeight);
        const ctx = canvas.getContext('2d');
        ctx.save();
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(
            video,
            cropX,
            cropY,
            cropWidth,
            cropHeight,
            0,
            0,
            canvas.width,
            canvas.height
        );
        ctx.restore();
        const image = canvas.toDataURL('image/jpeg', 0.92);
        setPreview(image);
        setCaptured(true);
        onChange('selfieDone', true);
        onChange('selfieImage', image);
        onChange('selfieCapturedAt', new Date().toISOString());
        onChange('selfieUploaded', false);
        stopCamera();
    };

    // Persist the selfie server-side so it becomes the profile photo. This runs for a
    // fresh capture and also backfills selfies captured before uploading existed.
    // A failure must not block the journey — the image is already in journey data.
    useEffect(() => {
        const image = data.selfieImage;
        if (!captured || !image || data.selfieUploaded || !userId) return;
        if (selfieUploadRef.current === image) return;
        selfieUploadRef.current = image;

        let active = true;
        (async () => {
            try {
                const response = await fetch(`${API}/api/profile/selfie`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {})
                    },
                    body: JSON.stringify({ userId, image, source: 'kyc_selfie' })
                });
                const json = await response.json();
                if (!response.ok || !json.success) throw new Error(json.message || 'Could not save the selfie.');
                if (!active) return;
                setPhotoSaveError('');
                onChange('selfieUploaded', true);
            } catch (err) {
                if (!active) return;
                selfieUploadRef.current = '';
                setPhotoSaveError('Selfie saved for this application, but we could not set it as your profile picture.');
            }
        })();

        return () => { active = false; };
    }, [captured, data.selfieImage, data.selfieUploaded, userId, token, onChange]);

    const startLivenessCheck = () => {
        setLivenessStatus('scanning');
        setTimeout(() => {
            captureSelfie();
            setLivenessStatus('captured');
        }, 3000);
    };

    const retakeSelfie = async () => {
        setCaptured(false);
        setLivenessStatus('idle');
        setPreview('');
        setPhotoSaveError('');
        selfieUploadRef.current = '';
        onChange('selfieDone', false);
        onChange('selfieImage', '');
        onChange('selfieCapturedAt', null);
        onChange('selfieUploaded', false);
        await startCamera();
    };

    if (!selfieStep) return <StepDisabledNotice title="Selfie Verification" />;

    return (
        <div className="alp-step-body">
            <div className="alp-selfie-card">
                {error && (
                    <div className="alp-alert alp-aa-error" style={{ marginBottom: '1rem' }}>
                        <strong>Camera</strong> {error}
                    </div>
                )}

                {photoSaveError && (
                    <div className="alp-alert alp-alert-warning" style={{ marginBottom: '1rem' }}>
                        <strong>Profile picture</strong> {photoSaveError}
                    </div>
                )}

                {!captured ? (
                    <div style={{ display: 'grid', gap: '1rem' }}>
                        <div style={{
                            position: 'relative',
                            width: 'min(100%, 3in)',
                            aspectRatio: '3 / 4',
                            margin: '0 auto',
                            borderRadius: '18px',
                            overflow: 'hidden',
                            border: '1px solid #fbcfe8',
                            background: '#0f172a'
                        }}
                            ref={previewFrameRef}>
                            <video
                                ref={videoRef}
                                playsInline
                                muted
                                autoPlay
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                    transform: 'scaleX(-1)'
                                }}
                            />
                            {cameraReady && (
                                <>
                                    <svg viewBox="0 0 100 133" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 10 }}>
                                        <defs>
                                            <mask id="oval-mask">
                                                <rect width="100" height="133" fill="white" />
                                                <ellipse cx="50" cy="55" rx="32" ry="42" fill="black" />
                                            </mask>
                                        </defs>
                                        <rect width="100" height="133" fill="rgba(0,0,0,0.65)" mask="url(#oval-mask)" />
                                        <ellipse cx="50" cy="55" rx="32" ry="42" fill="none" stroke={livenessStatus === 'scanning' ? '#10b981' : '#f43f5e'} strokeWidth="1" strokeDasharray={livenessStatus === 'scanning' ? "8 4" : "none"} />
                                    </svg>
                                    {livenessStatus === 'scanning' && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '13%',
                                            left: '18%',
                                            width: '64%',
                                            height: '2px',
                                            background: '#10b981',
                                            boxShadow: '0 0 8px 2px rgba(16, 185, 129, 0.6)',
                                            zIndex: 20,
                                            animation: 'kyc-scan 1.5s ease-in-out infinite alternate'
                                        }} />
                                    )}
                                    <div style={{
                                        position: 'absolute',
                                        bottom: '6%',
                                        left: 0,
                                        width: '100%',
                                        textAlign: 'center',
                                        color: 'white',
                                        zIndex: 20,
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                        textShadow: '0 1px 3px rgba(0,0,0,0.8)'
                                    }}>
                                        {livenessStatus === 'scanning' ? 'Analyzing liveliness...' : 'Align face in the oval'}
                                    </div>
                                </>
                            )}
                            {!cameraReady && (
                                <div style={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'grid',
                                    placeItems: 'center',
                                    color: 'white',
                                    background: 'rgba(15,23,42,0.35)',
                                    fontSize: '0.95rem',
                                    fontWeight: 600
                                }}>
                                    {loading ? 'Starting camera…' : 'Camera preview loading…'}
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                            <button className="alp-btn-primary" onClick={startLivenessCheck} disabled={!cameraReady || livenessStatus === 'scanning'} style={{ minWidth: '160px' }}>
                                {livenessStatus === 'scanning' ? 'Please hold steady...' : 'I am ready'}
                            </button>
                            <button className="alp-btn-ghost" onClick={startCamera} disabled={loading || livenessStatus === 'scanning'} style={{ minWidth: '140px' }}>
                                {loading ? 'Restarting…' : 'Restart Camera'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: '1rem' }}>
                        <div style={{
                            width: 'min(100%, 3in)',
                            aspectRatio: '3 / 4',
                            margin: '0 auto',
                            borderRadius: '18px',
                            overflow: 'hidden',
                            border: '1px solid #bbf7d0',
                            background: '#f0fdf4'
                        }}>
                            <img src={preview} alt="Captured selfie" style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }} />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button className="alp-btn-ghost" onClick={retakeSelfie} style={{ minWidth: '140px' }}>
                                Retake
                            </button>
                        </div>
                    </div>
                )}

                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <style>{`
                    @keyframes kyc-scan {
                        from { top: 13%; }
                        to { top: 73%; }
                    }
                `}</style>
            </div>
        </div>
    );
}

function StepBankVerification({ data, onChange }) {
    const [accountNumber, setAccountNumber] = useState(data.bankAccountNumber || '');
    const [ifscCode, setIfscCode] = useState(data.bankIfscCode || '');
    const [message, setMessage] = useState(data.bankDone ? 'Bank account verified.' : '');
    const [verified, setVerified] = useState(Boolean(data.bankDone));
    const [savedBank, setSavedBank] = useState(null);
    const [savedLoading, setSavedLoading] = useState(false);
    const [savedError, setSavedError] = useState('');
    const session = JSON.parse(localStorage.getItem('userSession') || '{}');
    const userId = session?.userId;
    const token = session?.token || '';
    const API_URL = process.env.REACT_APP_API_URL || '';
    const { bankStep } = useFeatureFlags();

    useEffect(() => {
        if (data.bankDone) {
            setVerified(true);
            setMessage('Bank account verified.');
        }
    }, [data.bankDone]);

    useEffect(() => {
        if (!bankStep && !data.bankDone) onChange('bankDone', true);
    }, [bankStep, data.bankDone, onChange]);

    useEffect(() => {
        let active = true;

        const loadSavedBank = async () => {
            if (!userId) return;

            setSavedLoading(true);
            setSavedError('');

            try {
                const response = await fetch(`${API_URL}/api/penny-drop/last`, {
                    credentials: 'include',
                    headers: {
                        ...(token ? { Authorization: `Bearer ${token}` } : {})
                    }
                });
                const json = await response.json().catch(() => ({}));
                if (!response.ok || !json.success || !active) return;

                const record = json.record || null;
                if (!record) {
                    setSavedBank(null);
                    return;
                }

                setSavedBank({
                    accountNumber: String(record.account_number || ''),
                    ifscCode: String(record.ifsc_code || ''),
                    beneficiaryName: record.beneficiary_name || 'JOHN DOE',
                    status: record.status || 'VERIFIED'
                });
            } catch (err) {
                if (active) {
                    setSavedError('Could not load saved bank details.');
                }
            } finally {
                if (active) setSavedLoading(false);
            }
        };

        loadSavedBank();
        return () => {
            active = false;
        };
    }, [API_URL, token, userId]);

    // Fast-track reloan reusing the same disbursal account we already penny-drop
    // verified last cycle: fill the form and mark it done straight from the saved
    // record, with no fresh Signzy call — submission still independently re-checks
    // this same penny_drop row (assertUserHasVerifiedPennyDrop), so this is safe.
    // "Use a different account" / manual entry below is untouched and still works.
    useEffect(() => {
        if (!data.isFastTrackReloan || data.bankDone) return;
        if (savedBank?.status !== 'VERIFIED' || !savedBank.accountNumber || !savedBank.ifscCode) return;
        setAccountNumber(savedBank.accountNumber);
        setIfscCode(savedBank.ifscCode);
        setVerified(true);
        setMessage('Using your previously verified bank account.');
        onChange('bankAccountNumber', savedBank.accountNumber);
        onChange('bankIfscCode', savedBank.ifscCode);
        onChange('bankBeneficiaryName', savedBank.beneficiaryName || '');
        onChange('bankDone', true);
    }, [data.isFastTrackReloan, data.bankDone, savedBank, onChange]);

    // Bank data the Account Aggregator already pulled in — used to pre-fill this form
    // so the applicant isn't retyping details we already have.
    const [aaLinkedBank, setAaLinkedBank] = useState(null);

    useEffect(() => {
        let active = true;

        const loadAaLinkedBank = async () => {
            if (!userId) return;
            try {
                const response = await fetch(`${API_URL}/api/aa/linked-bank-details`, {
                    credentials: 'include',
                    headers: {
                        ...(token ? { Authorization: `Bearer ${token}` } : {})
                    }
                });
                const json = await response.json().catch(() => ({}));
                if (!response.ok || !json.success || !active || !json.hasData) return;

                setAaLinkedBank({
                    accountNumber: json.accountNumber || '',
                    ifscCode: json.ifscCode || '',
                    bankName: json.bankName || '',
                    accountNumberHint: json.accountNumberHint || ''
                });
            } catch {
                // Best-effort only; manual entry still works if this fails.
            }
        };

        loadAaLinkedBank();
        return () => {
            active = false;
        };
    }, [API_URL, token, userId]);

    // Pre-fill once, and only if nothing else (a saved verified account, or the
    // applicant's own typing) has already put something in these fields.
    useEffect(() => {
        if (verified || accountNumber || ifscCode || savedBank || !aaLinkedBank) return;
        if (aaLinkedBank.ifscCode) {
            setIfscCode(aaLinkedBank.ifscCode);
            onChange('bankIfscCode', aaLinkedBank.ifscCode);
        }
        if (aaLinkedBank.accountNumber) {
            setAccountNumber(aaLinkedBank.accountNumber);
            onChange('bankAccountNumber', aaLinkedBank.accountNumber);
        }
    }, [aaLinkedBank, verified, accountNumber, ifscCode, savedBank, onChange]);

    const persistVerification = useCallback(async (cleanedAccount, cleanedIfsc) => {
        if (!userId) {
            throw new Error('Session expired. Please login again.');
        }

        const response = await fetch(`${API_URL}/api/penny-drop`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
                accountNumber: cleanedAccount,
                ifsc: cleanedIfsc
            })
        });

        const json = await response.json().catch(() => ({}));
        if (!response.ok || !json.success) {
            throw new Error(json.message || 'Unable to verify bank account.');
        }

        setSavedBank({
            accountNumber: cleanedAccount,
            ifscCode: cleanedIfsc,
            beneficiaryName: json.record?.beneficiaryName || json.beneficiaryName || 'JOHN DOE',
            status: json.record?.status || 'VERIFIED'
        });
        return json;
    }, [API_URL, token, userId]);

    const handleVerify = async () => {
        const cleanedAccount = normalizeAccountNumber(accountNumber);
        const cleanedIfsc = normalizeIfscCode(ifscCode);

        if (!/^\d{9,18}$/.test(cleanedAccount)) {
            setVerified(false);
            setMessage('Enter a valid account number.');
            return;
        }

        if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(cleanedIfsc)) {
            setVerified(false);
            setMessage('Enter a valid IFSC code.');
            return;
        }

        try {
            setMessage('Verifying bank account...');
            const verification = await persistVerification(cleanedAccount, cleanedIfsc);
            onChange('bankAccountNumber', cleanedAccount);
            onChange('bankIfscCode', cleanedIfsc);
            onChange('bankBeneficiaryName', verification.record?.beneficiaryName || verification.beneficiaryName || '');
            onChange('bankDone', true);
            setVerified(true);
            setMessage('Bank account verified. You can continue.');
        } catch (err) {
            setVerified(false);
            onChange('bankDone', false);
            setMessage(err.message || 'Unable to verify bank account.');
        }
    };

    const handleUseLastSaved = async () => {
        if (!savedBank?.accountNumber || !savedBank?.ifscCode) {
            setMessage('No saved bank details found yet.');
            return;
        }

        const nextAccount = savedBank.accountNumber;
        const nextIfsc = savedBank.ifscCode;
        setAccountNumber(nextAccount);
        setIfscCode(nextIfsc);
        setVerified(false);
        setMessage('Re-verifying your saved bank details...');

        try {
            const verification = await persistVerification(nextAccount, nextIfsc);
            onChange('bankAccountNumber', nextAccount);
            onChange('bankIfscCode', nextIfsc);
            onChange('bankBeneficiaryName', verification.record?.beneficiaryName || verification.beneficiaryName || '');
            onChange('bankDone', true);
            setVerified(true);
            setMessage('Saved bank account verified. You can continue.');
        } catch (err) {
            onChange('bankDone', false);
            setMessage(err.message || 'Saved bank account could not be verified. Please try again.');
        }
    };

    const handleChangeAccount = (value) => {
        const nextValue = value.replace(/\D/g, '');
        setAccountNumber(nextValue);
        setVerified(false);
        onChange('bankAccountNumber', nextValue);
        onChange('bankDone', false);
    };

    const handleChangeIfsc = (value) => {
        const nextValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
        setIfscCode(nextValue);
        setVerified(false);
        onChange('bankIfscCode', nextValue);
        onChange('bankDone', false);
    };

    if (!bankStep) return <StepDisabledNotice title="Bank Verification" />;

    return (
        <div className="alp-step-body">
            <div className="alp-bank-grid">
                <div className="alp-bank-panel">
                    <div className="alp-bank-panel-head">
                        <div className="alp-bank-panel-icon">💳</div>
                        <div>
                            <div className="alp-bank-kicker">Bank Verification</div>
                            <h3>Verify disbursal account</h3>
                        </div>
                    </div>

                    <div className="alp-bank-fields alp-bank-fields-2col">
                        <div className="alp-field">
                            <label>Account Number</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                placeholder="Enter Account Number"
                                value={accountNumber}
                                onChange={e => handleChangeAccount(e.target.value)}
                            />
                            {!accountNumber && !savedBank && aaLinkedBank?.accountNumberHint && (
                                <span className="alp-hint" style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem', display: 'block' }}>
                                    Linked via Account Aggregator: {aaLinkedBank.accountNumberHint}
                                </span>
                            )}
                        </div>
                        <div className="alp-field">
                            <label>IFSC Code</label>
                            <input
                                type="text"
                                placeholder="Enter IFSC Code"
                                value={ifscCode}
                                onChange={e => handleChangeIfsc(e.target.value)}
                                style={{ textTransform: 'uppercase' }}
                            />
                        </div>
                    </div>

                    <p className="alp-bank-note">
                        We send ₹1 to this account to confirm it belongs to you. It is refunded instantly.
                    </p>

                    {message && (
                        <div className={`alp-alert ${verified ? 'alp-alert-success' : 'alp-aa-error'}`} style={{ marginTop: '0.8rem' }}>
                            <strong>{verified ? 'Verified' : 'Check'}</strong> {message}
                        </div>
                    )}

                    <div className="alp-bank-actions">
                        <button className="alp-btn-primary" onClick={handleVerify}>
                            {verified ? 'Verified' : 'Verify Bank'}
                        </button>
                    </div>
                </div>

                <div className="alp-bank-panel alp-bank-panel-saved">
                    <div className="alp-bank-kicker">Saved Details</div>
                    <h3>Use last verified account</h3>
                    {savedLoading ? (
                        <p>Checking for a saved account…</p>
                    ) : savedBank ? (
                        <div className="alp-bank-saved-list">
                            <div><span>Account</span><strong>{savedBank.accountNumber}</strong></div>
                            <div><span>IFSC</span><strong>{savedBank.ifscCode}</strong></div>
                            <div><span>Name</span><strong>{savedBank.beneficiaryName}</strong></div>
                            <div><span>Status</span><strong>{savedBank.status || 'VERIFIED'}</strong></div>
                        </div>
                    ) : (
                        <div className="alp-bank-saved-empty">
                            <span className="alp-bank-saved-empty-icon">🏦</span>
                            <p>No saved account yet. Verify one on the left and we'll reuse it next time.</p>
                        </div>
                    )}

                    {savedError && (
                        <div className="alp-alert alp-aa-error" style={{ marginTop: '0.75rem' }}>
                            <strong>Saved</strong> {savedError}
                        </div>
                    )}

                    {!!savedBank && (
                        <div className="alp-bank-actions">
                            <button
                                type="button"
                                className="alp-btn-ghost"
                                onClick={handleUseLastSaved}
                                disabled={!savedBank?.accountNumber || !savedBank?.ifscCode}
                            >
                                Use Saved Details
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function StepAccountAggregator({ data, onChange }) {
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [uploadMsg, setUploadMsg] = useState('');
    const [hasBankStatement, setHasBankStatement] = useState(Boolean(data.aaManualDone));
    const [, setStatus] = useState(data.aaStatus || 'idle');
    const [sessionInfo, setSessionInfo] = useState(data.aaSession || null);
    const [aaModalOpen, setAaModalOpen] = useState(false);
    const [aaStartTime, setAaStartTime] = useState(data.aaStartTime || null);
    const [timeoutPopupOpen, setTimeoutPopupOpen] = useState(false);
    const [editingMobile, setEditingMobile] = useState(false);
    const [mobileDraft, setMobileDraft] = useState('');
    const [mobileError, setMobileError] = useState('');
    const session = JSON.parse(localStorage.getItem('userSession') || '{}');
    const userId = session?.userId;
    // aaMobile is the applicant's override; it is what the consent OTP is sent to.
    const profileMobile = toIndianMobile(session?.phone || data.mobile || '');
    const mobile = toIndianMobile(data.aaMobile) || profileMobile;
    const mobileValid = INDIAN_MOBILE_RE.test(mobile);
    const email = session?.email || data.email || '';
    const token = session?.token || '';
    const { aaStep } = useFeatureFlags();
    const selectedAaMethod = data.aaMethod || (hasBankStatement ? 'manual_upload' : 'account_aggregator');
    // Manual upload stays hidden until the applicant has actually tried the
    // Account Aggregator: it unlocks after an AA failure, a 3-min data-fetch
    // timeout, or if a statement was already uploaded. Keeps AA the primary path.
    const manualUnlocked = Boolean(data.aaManualUnlocked) || hasBankStatement || Boolean(data.aaManualDone);

    const syncUploadedStatement = useCallback(async () => {
        if (!userId) return;

        try {
            const response = await fetch(`${API}/api/documents/${userId}?requestingUserId=${userId}&requestingUserRole=${session?.role || 'user'}`);
            const json = await response.json();
            if (!response.ok || !json.success) return;

            const docs = Array.isArray(json.documents) ? json.documents : [];
            const bankDocs = docs.filter(doc => {
                const type = String(doc.document_type || doc.type || '').toLowerCase();
                return type === 'bank statement' || type === 'bank_statement';
            });
            const found = bankDocs.length > 0;

            if (found) {
                setHasBankStatement(true);
                onChange('aaMethod', 'manual_upload');
                onChange('aaStatus', 'manual_uploaded');
                onChange('aaManualDone', true);
                onChange('aaDone', true);
                setStatus('manual_uploaded');
                if (!uploadMsg) setUploadMsg('');
            }
        } catch {
            // Best-effort sync only.
        }
    }, [onChange, session?.role, uploadMsg, userId]);

    // --- Manual fallback: a single bank account statement (last 6 months) ---------
    // Always offered alongside Account Aggregator as an alternative path. A real
    // bank statement is one continuous PDF covering the period, not per-month files.
    const statementInputRef = useRef(null);
    const [accountStatement, setAccountStatement] = useState(() => (data.accountStatement && data.accountStatement.fileName) ? data.accountStatement : null);
    const [statementPassword, setStatementPassword] = useState(data.accountStatementPassword || '');

    const handleStatementPasswordChange = (value) => {
        setStatementPassword(value);
        onChange('accountStatementPassword', value);
    };

    const triggerStatementUpload = () => {
        setUploadMsg('');
        statementInputRef.current?.click();
    };

    const handleStatementUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        const okType = file.type === 'application/pdf' || file.type.startsWith('image/');
        if (!okType) { setUploadMsg('Please upload a PDF or image (JPG/PNG) account statement.'); return; }
        if (file.size > 10 * 1048576) { setUploadMsg('File too large (max 10MB).'); return; }
        if (!userId) { setUploadMsg('Login session missing. Please sign in again.'); return; }

        setUploading(true);
        setUploadMsg('Uploading account statement…');
        try {
            const documentType = 'Account Statement';
            const presignRes = await fetch(`${API}/api/documents/presigned-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
                body: JSON.stringify({ fileName: file.name, fileType: file.type || 'application/pdf', documentType, userId })
            });
            if (!presignRes.ok) { const er = await presignRes.json().catch(() => ({})); throw new Error(er.message || `Failed to initialize upload (${presignRes.status})`); }
            const { presignedUrl, s3Key } = await presignRes.json();

            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('PUT', presignedUrl);
                xhr.setRequestHeader('Content-Type', file.type || 'application/pdf');
                xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`S3 Upload failed (Status ${xhr.status})`));
                xhr.onerror = () => reject(new Error('S3 Upload failed (Network Error)'));
                xhr.send(file);
            });

            // Password goes in the same request as the upload itself now —
            // it used to be a separate fire-and-forget call made afterward
            // (deliberately ignoring its failure since "the statement is
            // already saved either way"), which meant a password the
            // customer actually typed could silently never reach the row if
            // that second request failed for any reason (network blip,
            // timeout, tab closed right after upload). One atomic call can't
            // half-fail that way.
            const trimmedPassword = statementPassword.trim();
            const confirmRes = await fetch(`${API}/api/documents/confirm-upload`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
                body: JSON.stringify({ s3Key, originalName: file.name, documentType, fileSize: file.size, userId, password: trimmedPassword || undefined })
            });
            const cj = await confirmRes.json().catch(() => ({}));
            if (!confirmRes.ok || !cj.success) throw new Error(cj.message || 'Upload failed.');

            const uploaded = { fileName: String(cj?.document?.fileName || file.name).trim(), docId: cj?.document?.id || null };
            setAccountStatement(uploaded);
            onChange('accountStatement', uploaded);
            setUploadMsg('');

            onChange('aaMethod', 'manual_upload');
            onChange('aaStatus', 'manual_uploaded');
            onChange('aaManualDone', true);
            onChange('aaDone', true);
            setStatus('manual_uploaded');
            setTimeoutPopupOpen(false);
        } catch (err) {
            setUploadMsg(err.message || 'Upload failed.');
        } finally {
            setUploading(false);
        }
    };

    const startAggregator = useCallback(async () => {
        if (!userId) {
            setError('Login session missing. Please sign in again.');
            setStatus('error');
            onChange('aaStatus', 'error');
            return;
        }

        // The provider sends the consent OTP to this number, so never fall back to a placeholder.
        if (!INDIAN_MOBILE_RE.test(mobile)) {
            setError('Enter a valid 10-digit mobile number before starting Account Aggregator.');
            setStatus('error');
            onChange('aaStatus', 'error');
            return;
        }

        setLoading(true);
        setError('');
        setStatus('starting');
        onChange('aaStatus', 'starting');
        const now = Date.now();
        setAaStartTime(now);
        onChange('aaStartTime', now);
        setTimeoutPopupOpen(false);

        try {
            const response = await fetch(`${API}/api/moneyone/consent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    userId,
                    mobile,
                    email,
                    loanId: data.loanId || null,
                    vua: `${mobile}@bank`,
                    partyIdentifierValue: mobile,
                    fetchType: 'PERIODIC',
                    months: 6,
                    frequencyUnit: 'MONTH',
                    frequencyValue: 1
                })
            });

            const json = await response.json();
            if (json.success) {
                const nextSession = {
                    sessionId: json.sessionId,
                    consentHandle: json.consentHandle,
                    consentId: json.consentId,
                    redirectUrl: json.redirectUrl
                };
                onChange('aaSession', nextSession);
                setSessionInfo(nextSession);

                if (String(json.status || '').toUpperCase() === 'ERROR') {
                    console.error('Account Aggregator API returned ERROR status:', json);
                    setError('Account Aggregator provider rejected the request. Please try again.');
                    setAaModalOpen(false);
                    onChange('aaManualUnlocked', true);
                } else if (json.redirectUrl) {
                    setAaModalOpen(true);
                }

                onChange('aaMethod', 'account_aggregator');
                onChange('aaStatus', 'pending_consent');
                onChange('aaDone', false);
                setStatus('pending_consent');
            } else {
                throw new Error(json.message || 'Unable to start Account Aggregator');
            }


        } catch (err) {
            setError(err.message || 'Unable to start Account Aggregator');
            setStatus('error');
            onChange('aaStatus', 'error');
            onChange('aaManualUnlocked', true);
        } finally {
            setLoading(false);
        }
    }, [data.loanId, email, mobile, onChange, userId, token]);

    const saveMobile = () => {
        const next = toIndianMobile(mobileDraft);
        if (!INDIAN_MOBILE_RE.test(next)) {
            setMobileError('Enter a valid 10-digit mobile number.');
            return;
        }
        if (next !== mobile) {
            // Any consent already raised belongs to the previous number — discard it
            // so the next Start raises a fresh consent against the new one.
            onChange('aaSession', null);
            onChange('aaStatus', 'idle');
            onChange('aaDone', false);
            setSessionInfo(null);
            setStatus('idle');
            setError('');
        }
        onChange('aaMobile', next);
        setEditingMobile(false);
        setMobileError('');
    };



    const refreshStatus = useCallback(async () => {
        if (!sessionInfo?.sessionId && !sessionInfo?.consentId && !sessionInfo?.consentHandle) return;

        try {
            const params = new URLSearchParams();
            if (sessionInfo.sessionId) params.set('sessionId', sessionInfo.sessionId);
            if (sessionInfo.consentId) params.set('consentId', sessionInfo.consentId);
            if (sessionInfo.consentHandle) params.set('consentHandle', sessionInfo.consentHandle);
            const response = await fetch(`${API}/api/moneyone/session-status?${params.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const json = await response.json();
            console.log('🔄 Polling status response:', json);
            if (response.ok && json.success) {
                const updatedSession = { ...sessionInfo, ...json.session };
                onChange('aaSession', updatedSession);
                setSessionInfo(updatedSession);
                const cs = String(json.session?.consent_status || '').toUpperCase();
                const fds = String(json.session?.fi_data_status || '').toUpperCase();
                console.log('✅ Polling extracted status:', { cs, fds });
                if (fds === 'FETCHED') {
                    console.log('🎉 Bank data fetched! Updating UI...');
                    onChange('aaDone', true);
                    onChange('aaStatus', 'fetched');
                    setStatus('fetched');
                } else if (cs === 'ACTIVE' || cs === 'CONSENT_APPROVED' || cs === 'COMPLETED' || cs === 'SUCCESS') {
                    // Consent approved is enough to move through the journey — the data
                    // fetches in the background. If it still hasn't arrived by the submit
                    // step, handleFinalSubmit routes the client to the manual statement upload.
                    console.log('✅ Consent verified, waiting for bank data...');
                    onChange('aaDone', true);
                    onChange('aaStatus', 'fetching_data');
                    setStatus('fetching_data');
                }
            } else {
                console.warn('⚠️ Polling failed:', response.status, json);
            }
        } catch (err) {
            console.error('❌ Polling error:', err);
            // Keep the UI stable; status refresh is best-effort.
        }
    }, [onChange, sessionInfo, token]);

    useEffect(() => {
        if (data.aaManualDone || hasBankStatement) {
            setStatus('manual_uploaded');
        } else if (data.aaDone) {
            setStatus(data.aaStatus === 'manual_uploaded' ? 'manual_uploaded' : 'linked');
            setSessionInfo(data.aaSession || null);
        }
    }, [data.aaDone, data.aaManualDone, data.aaSession, data.aaStatus, hasBankStatement]);

    useEffect(() => {
        syncUploadedStatement();
    }, [syncUploadedStatement]);

    useEffect(() => {
        if (!aaStep && !data.aaDone) onChange('aaDone', true);
    }, [aaStep, data.aaDone, onChange]);

    useEffect(() => {
        if (!sessionInfo) return;
        // Once a statement has been uploaded manually, the applicant has already
        // resolved the "no bank data" situation — stop re-checking the AA timeout,
        // otherwise this reopens the "Bank data not received" popup every 5s forever
        // (fi_data_status never becomes FETCHED once they've moved to manual upload).
        if (hasBankStatement || data.aaManualDone) return;

        const checkTimeout = () => {
            // If 3 min pass after starting AA and the bank push data still
            // hasn't arrived — and the applicant isn't mid-consent in the iframe —
            // unlock the manual account-statement upload as a fallback and nudge
            // them to retry the aggregator if they'd rather do that.
            if (aaStartTime && !aaModalOpen) {
                const sessionDataLocal = sessionInfo || data.aaSession || {};
                const fiDataStatusLocal = String(sessionDataLocal?.fi_data_status || '').toUpperCase();
                if (fiDataStatusLocal !== 'FETCHED') {
                    const elapsed = Date.now() - aaStartTime;
                    if (elapsed > 180000) { // 180,000 ms = 3 min
                        setTimeoutPopupOpen(true);
                        onChange('aaManualUnlocked', true);
                    }
                }
            }
        };

        const iv = setInterval(() => {
            refreshStatus();
            checkTimeout();
        }, 5000);
        return () => clearInterval(iv);
    }, [refreshStatus, sessionInfo, aaStartTime, data, onChange, aaModalOpen, hasBankStatement]);

    const sessionData = sessionInfo || data.aaSession || {};
    const consentStatus = String(sessionData?.consent_status || '').toUpperCase();
    const fiDataStatus = String(sessionData?.fi_data_status || '').toUpperCase();
    const consentReceived = consentStatus === 'ACTIVE' || consentStatus === 'CONSENT_APPROVED' || consentStatus === 'COMPLETED' || consentStatus === 'SUCCESS';
    const dataFetched = fiDataStatus === 'FETCHED';

    // Removed auto-close useEffect to allow the CAMS iframe to redirect naturally.

    useEffect(() => {
        const handleMessage = async (event) => {
            if (event.data === 'AA_CONSENT_SUCCESS' || event.data?.type === 'AA_CONSENT_SUCCESS') {
                console.log('🎉 [AA] Received success message from iframe, closing modal and triggering data fetch...');
                setAaModalOpen(false);

                if (sessionInfo) {
                    try {
                        await fetch(`${API}/api/moneyone/mark-consent-success`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                sessionId: sessionInfo.sessionId,
                                consentHandle: sessionInfo.consentHandle,
                                consentId: sessionInfo.consentId
                            })
                        });
                    } catch (err) {
                        console.error('Error marking consent success:', err);
                    }
                }

                refreshStatus(); // trigger an immediate poll
            } else if (event.data === 'AA_CONSENT_FAILED' || event.data?.type === 'AA_CONSENT_FAILED') {
                console.error('❌ [AA] Received failed message from iframe.');
                setAaModalOpen(false);
                setError('Account Aggregator consent process failed or was cancelled. Please try again.');
                onChange('aaStatus', 'failed');
                setStatus('failed');
                onChange('aaManualUnlocked', true);
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [refreshStatus, onChange, sessionInfo, token]);

    if (!aaStep) return <StepDisabledNotice title="Account Aggregator" />;

    return (
        <div className="alp-step-body">
            <div className="alp-aa-header-intro">
                <h3>Provide Bank Statements</h3>
                <p>{manualUnlocked
                    ? "Bank data wasn't received automatically. Upload your bank account statement (last 6 months) to continue."
                    : "Securely link your bank account to fetch your statements automatically, or upload your bank account statement (last 6 months) manually."}</p>
            </div>

            <div className="alp-aa-choices-grid">
                <div
                    className={`alp-aa-choice-card recommended ${selectedAaMethod !== 'manual_upload' ? 'active' : ''}`}
                    onClick={() => { onChange('aaMethod', 'account_aggregator'); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onChange('aaMethod', 'account_aggregator')}
                    style={{ opacity: selectedAaMethod === 'manual_upload' ? 0.5 : 1, cursor: 'pointer' }}
                >
                    <h4>Account Aggregator</h4>
                    <p>Securely link your bank account. No paperwork needed.</p>
                    <div className="alp-aa-metric-group">
                        <div className="alp-aa-metric">
                            <span>Mobile No.</span>
                            {editingMobile ? (
                                <div className="alp-aa-mobile-edit" onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="tel"
                                        inputMode="numeric"
                                        maxLength={10}
                                        autoFocus
                                        placeholder="10-digit mobile"
                                        value={mobileDraft}
                                        onChange={(e) => {
                                            setMobileDraft(e.target.value.replace(/\D/g, '').slice(0, 10));
                                            setMobileError('');
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') saveMobile();
                                            if (e.key === 'Escape') { setEditingMobile(false); setMobileError(''); }
                                        }}
                                    />
                                    <button type="button" className="alp-aa-mobile-save" onClick={saveMobile}>Save</button>
                                    <button
                                        type="button"
                                        className="alp-aa-mobile-cancel"
                                        onClick={() => { setEditingMobile(false); setMobileError(''); }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <div className="alp-aa-mobile-view">
                                    <strong className={mobileValid ? '' : 'alp-aa-mobile-missing'}>
                                        {mobile || 'Not found'}
                                    </strong>
                                    <button
                                        type="button"
                                        className="alp-aa-mobile-edit-btn"
                                        disabled={consentReceived || dataFetched || loading}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMobileDraft(mobile);
                                            setMobileError('');
                                            setEditingMobile(true);
                                        }}
                                    >
                                        Edit
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {mobileError && <div className="alp-aa-mobile-error">{mobileError}</div>}
                    <div className="alp-aa-mobile-note">
                        The consent OTP is sent to this number.
                        {toIndianMobile(data.aaMobile) && toIndianMobile(data.aaMobile) !== profileMobile
                            ? ' Using an edited number instead of your registered one.'
                            : ''}
                    </div>
                    {(consentReceived || dataFetched) && (
                        <div className={`alp-alert alp-aa-pending-alert ${dataFetched ? 'alp-alert-success' : 'alp-alert-warning'}`} style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'space-between', ...(dataFetched ? {} : { background: '#fffbeb', border: '1px solid #fef3c7' }) }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                                {dataFetched ? (
                                    <span style={{ fontSize: '1.1rem' }}>✅</span>
                                ) : (
                                    <div style={{ display: 'flex', gap: '3px', alignItems: 'center', height: '18px', flexShrink: 0 }}>
                                        <div style={{ width: '4px', height: '12px', background: '#b45309', borderRadius: '2px', animation: 'pulse 1s infinite ease-in-out', animationDelay: '0s' }} />
                                        <div style={{ width: '4px', height: '12px', background: '#b45309', borderRadius: '2px', animation: 'pulse 1s infinite ease-in-out', animationDelay: '0.2s' }} />
                                        <div style={{ width: '4px', height: '12px', background: '#b45309', borderRadius: '2px', animation: 'pulse 1s infinite ease-in-out', animationDelay: '0.4s' }} />
                                    </div>
                                )}
                                <div style={{ minWidth: 0 }}>
                                    <strong style={{ color: dataFetched ? 'inherit' : '#b45309' }}>{dataFetched ? 'Bank Data Fetched' : 'Fetching Bank Data...'}</strong>
                                    <div style={{ fontSize: '0.82rem', opacity: 0.85, marginTop: '2px', color: dataFetched ? 'inherit' : '#b45309' }}>
                                        {dataFetched
                                            ? 'Bank statements received and processed successfully.'
                                            : 'Consent verified. Please wait while we securely pull your transactions.'}
                                    </div>
                                </div>
                            </div>
                            {!dataFetched && (
                                <button
                                    className="alp-btn-ghost"
                                    onClick={(e) => { e.stopPropagation(); onChange('aaSession', null); onChange('aaDone', false); startAggregator(); }}
                                    style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', border: '1px solid #fcd34d', borderRadius: '4px', background: 'white', color: '#b45309', whiteSpace: 'nowrap', flexShrink: 0 }}
                                >
                                    Restart
                                </button>
                            )}
                        </div>
                    )}
                    {sessionInfo?.redirectUrl && !consentReceived && (
                        <div className="alp-alert alp-alert-warning alp-aa-pending-alert" style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'space-between', background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '8px', padding: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                                <span style={{ fontSize: '1.1rem' }}>⏳</span>
                                <div style={{ minWidth: 0 }}>
                                    <strong style={{ color: '#b45309' }}>Pending Verification</strong>
                                    <div style={{ fontSize: '0.82rem', color: '#b45309', opacity: 0.85, marginTop: '2px' }}>Please complete the process in the window.</div>
                                </div>
                            </div>
                            <button
                                className="alp-btn-ghost"
                                onClick={(e) => { e.stopPropagation(); onChange('aaSession', null); onChange('aaDone', false); setSessionInfo(null); startAggregator(); }}
                                style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', border: '1px solid #fcd34d', borderRadius: '4px', background: 'white', color: '#b45309', whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                                Restart
                            </button>
                        </div>
                    )}
                    {error && selectedAaMethod !== 'manual_upload' && <div className="alp-alert alp-aa-error"><strong>Error</strong> {error}</div>}
                    <div className="alp-aa-choice-actions">
                        <button
                            className="alp-btn-primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                const isErrorStatus = String(sessionInfo?.consent_status || '').toUpperCase() === 'ERROR';
                                if (sessionInfo?.redirectUrl && !consentReceived && !isErrorStatus) {
                                    setAaModalOpen(true);
                                } else {
                                    if (isErrorStatus) {
                                        onChange('aaSession', null);
                                        setSessionInfo(null);
                                    }
                                    startAggregator();
                                }
                            }}
                            disabled={loading || dataFetched || consentReceived || selectedAaMethod === 'manual_upload' || editingMobile || !mobileValid}
                            style={{ width: '100%', opacity: (dataFetched || consentReceived || selectedAaMethod === 'manual_upload' || !mobileValid) ? 0.6 : 1, cursor: (dataFetched || consentReceived || selectedAaMethod === 'manual_upload' || !mobileValid) ? 'not-allowed' : 'pointer' }}
                        >
                            {dataFetched ? '✓ Data Fetched' : consentReceived ? 'Fetching Data...' : loading ? 'Starting AA…' : (sessionInfo?.redirectUrl ? 'Resume Account Aggregator' : 'Start Account Aggregator')}
                        </button>
                    </div>
                </div>

                <div
                    className={`alp-aa-choice-card alt ${selectedAaMethod === 'manual_upload' ? 'active' : ''} ${(hasBankStatement || accountStatement) ? 'uploaded' : ''}`}
                    onClick={() => { if (!dataFetched) onChange('aaMethod', 'manual_upload'); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !dataFetched && onChange('aaMethod', 'manual_upload')}
                    style={{ opacity: dataFetched ? 0.5 : 1, cursor: dataFetched ? 'not-allowed' : 'pointer' }}
                >
                    <h4>Manual Upload — Account Statement</h4>
                    <p>Upload your bank account statement covering the last 6 months — a single PDF or image, max 10MB.</p>
                    <div className="alp-statement-upload-row" onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: '8px', border: `1px solid ${accountStatement ? '#bbf7d0' : '#e2e8f0'}`, background: accountStatement ? '#f0fdf4' : '#f8fafc', marginTop: '0.5rem' }}>
                        <div className="alp-statement-upload-info" style={{ minWidth: 0, textAlign: 'left' }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#334155' }}>Last 6 months</div>
                            <div style={{ fontSize: '0.72rem', color: accountStatement ? '#15803d' : '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '220px' }}>
                                {accountStatement ? `✅ ${accountStatement.fileName || 'Uploaded'}` : 'Not uploaded'}
                            </div>
                        </div>
                        <button
                            type="button"
                            className="alp-btn-ghost alp-statement-upload-button"
                            onClick={(e) => { e.stopPropagation(); triggerStatementUpload(); }}
                            disabled={uploading}
                            style={{ padding: '0.35rem 0.75rem', fontSize: '0.78rem', flexShrink: 0 }}
                        >
                            {uploading ? 'Uploading…' : accountStatement ? 'Replace' : 'Upload'}
                        </button>
                    </div>
                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '0.6rem', textAlign: 'left' }}>
                        <label style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>
                            PDF password (if the statement is protected)
                        </label>
                        <input
                            type="text"
                            placeholder="Leave blank if not password-protected"
                            value={statementPassword}
                            onChange={(e) => handleStatementPasswordChange(e.target.value)}
                            style={{ width: '100%', padding: '0.45rem 0.6rem', fontSize: '0.8rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                        />
                    </div>
                    {uploadMsg && selectedAaMethod === 'manual_upload' && (
                        <div className="alp-upload-msg" style={{ marginTop: '0.5rem' }}>{uploadMsg}</div>
                    )}
                </div>
            </div>
            <input
                ref={statementInputRef}
                type="file"
                accept=".pdf,application/pdf,image/*"
                onChange={handleStatementUpload}
                style={{ display: 'none' }}
            />

            {aaModalOpen && sessionInfo?.redirectUrl && (
                <div className="alp-aa-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.7)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
                    <div className="alp-aa-modal-content" style={{ width: '100%', maxWidth: '440px', height: '90%', maxHeight: '720px', background: 'white', borderRadius: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
                        <div style={{ padding: '0.8rem 1.2rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <strong style={{ fontSize: '1.05rem', color: '#0f172a' }}>Account Aggregator</strong>
                            <button onClick={() => setAaModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b', padding: '0 0.2rem' }}>&times;</button>
                        </div>
                        <iframe src={sessionInfo.redirectUrl} style={{ width: '100%', flex: 1, border: 'none' }} title="Finduit AA"></iframe>
                    </div>
                </div>
            )}
            {timeoutPopupOpen && (
                <div className="alp-aa-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.7)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
                    <div className="alp-aa-modal-content" style={{ width: '90%', maxWidth: '400px', background: 'white', borderRadius: '16px', padding: '1.5rem', textAlign: 'center', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏱️</div>
                        <h3 style={{ color: '#0f172a', marginBottom: '0.5rem' }}>Bank data not received</h3>
                        <p style={{ color: '#475569', fontSize: '0.95rem', marginBottom: '1.5rem', lineHeight: '1.5' }}>
                            We did not receive the data from your bank for your credits and salary. You can retry the Account Aggregator, or upload your bank account statement (last 6 months) instead to continue right away.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <button className="alp-btn-primary" onClick={() => { setTimeoutPopupOpen(false); onChange('aaSession', null); setSessionInfo(null); startAggregator(); }}>
                                Run Aggregator Again
                            </button>
                            <button className="alp-btn-ghost" onClick={() => { setTimeoutPopupOpen(false); onChange('aaMethod', 'manual_upload'); }} style={{ border: '1px solid #cbd5e1' }}>
                                Upload Account Statement Instead
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <style>{`
                @keyframes pulse { 0%, 100% { transform: scaleY(0.5); opacity: 0.5; } 50% { transform: scaleY(1); opacity: 1; } }
            `}</style>
        </div>
    );
}

function StepAddressProof({ data, onChange }) {
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadFinalizing, setUploadFinalizing] = useState(false);
    const [docType, setDocType] = useState('');
    const [uploadMsg, setUploadMsg] = useState('');
    const [isLocationSaved, setIsLocationSaved] = useState(sessionStorage.getItem('locationFetched') === 'true');
    const session = JSON.parse(localStorage.getItem('userSession') || '{}');
    const userId = session?.userId;
    const token = session?.token || '';

    const [gpsLocationText, setGpsLocationText] = useState('');
    const [frontendMismatch, setFrontendMismatch] = useState(data.addressMismatch);
    const [frontendMatchChecked, setFrontendMatchChecked] = useState(false);
    const { addressProofStep: addressProofStepFlag, addressGeoMatch } = useFeatureFlags();
    // Fast-track reloan: reuse the durable address already on file instead of
    // asking again — same kill-switch the global feature flag already uses.
    const addressProofStep = data.isFastTrackReloan ? false : addressProofStepFlag;

    useEffect(() => {
        if (!addressProofStep && !data.addressProofDone) onChange('addressProofDone', true);
    }, [addressProofStep, data.addressProofDone, onChange]);

    useEffect(() => {
        if (!userId) return;
        if (!data.gpsLatitude || !data.aadhaarAddress) {
            fetch(`${API}/api/auth/profile/${userId}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
                .then(r => r.json())
                .then(kycData => {
                    if (kycData.success && kycData.kycRecords) {
                        onChange('addressMismatch', kycData.kycRecords.aadhaar?.addressMismatch || false);
                        onChange('aadhaarAddress', kycData.kycRecords.aadhaar?.address || '');
                        if (kycData.user?.lastLatitude) {
                            onChange('gpsLatitude', kycData.user.lastLatitude);
                            onChange('gpsLongitude', kycData.user.lastLongitude);
                        }
                    }
                }).catch(console.error);
        }
    }, [userId, token, data.gpsLatitude, data.aadhaarAddress, onChange]);

    // Geolocation-vs-Aadhaar-address mismatch check can be switched off via the
    // FEATURE_ADDRESS_GEO_MATCH env flag — when off, location is always treated
    // as matched so users are never asked for additional address proof.
    useEffect(() => {
        if (!addressGeoMatch) {
            setFrontendMismatch(false);
            setFrontendMatchChecked(true);
            return;
        }
        if (!data.gpsLatitude || !data.gpsLongitude) return;

        fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${data.gpsLatitude}&lon=${data.gpsLongitude}`)
            .then(r => r.json())
            .then(res => {
                const gPincode = res.address?.postcode || '';
                const gCity = res.address?.city || res.address?.state_district || res.address?.county || '';
                const gpsLocText = `${gCity ? gCity + ', ' : ''}${gPincode}`;
                setGpsLocationText(gpsLocText);

                const aadhaarAddressStr = data.aadhaarAddress || '';
                const aPincodeMatch = aadhaarAddressStr.match(/\b\d{6}\b/);
                const aPincode = aPincodeMatch ? aPincodeMatch[0] : '';

                let isMatch = false;
                if (gPincode && aPincode && gPincode === aPincode) {
                    isMatch = true;
                } else if (gCity && aadhaarAddressStr.toLowerCase().includes(gCity.toLowerCase())) {
                    isMatch = true;
                }

                setFrontendMismatch(!isMatch);
                setFrontendMatchChecked(true);
            })
            .catch(err => console.error('Geocoding error:', err));
    }, [addressGeoMatch, data.gpsLatitude, data.gpsLongitude, data.aadhaarAddress]);

    // If no mismatch, they don't need to do this
    const mismatch = frontendMatchChecked ? frontendMismatch : data.addressMismatch;
    const done = data.addressProofDone;
    const fileName = data.addressProofFileName;

    const fetchLocation = useCallback(() => {
        if (!userId) return;
        const saveLocationToBackend = (latitude, longitude, source = 'GPS') => {
            fetch(`${API}/api/auth/location`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                credentials: 'same-origin',
                body: JSON.stringify({ latitude, longitude })
            })
                .then(res => res.json())
                .then(resData => {
                    if (resData.success) {
                        sessionStorage.setItem('locationFetched', 'true');
                        setIsLocationSaved(true);

                        // RE-FETCH KYC STATUS TO UPDATE addressMismatch!
                        fetch(`${API}/api/auth/profile/${userId}`, {
                            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                        })
                            .then(r => r.json())
                            .then(kycData => {
                                if (kycData.success && kycData.kycRecords) {
                                    onChange('addressMismatch', kycData.kycRecords.aadhaar?.addressMismatch || false);
                                    onChange('aadhaarAddress', kycData.kycRecords.aadhaar?.address || '');
                                    onChange('gpsLatitude', latitude);
                                    onChange('gpsLongitude', longitude);
                                }
                            }).catch(console.error);

                    } else {
                        console.warn(`📍 [LOCATION] Server rejected location:`, resData.message);
                    }
                })
                .catch(err => console.error(`📍 [LOCATION] Network error saving location:`, err));
        };

        const fetchIpLocationFallback = () => {
            alert('Location Error: GPS location is required. Please enable location permissions in your browser to proceed.');
        };

        if (!navigator.geolocation) {
            fetchIpLocationFallback();
            return;
        }

        let isResolved = false;
        const gpsTimeout = setTimeout(() => {
            if (!isResolved) {
                isResolved = true;
                fetchIpLocationFallback();
            }
        }, 6000);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                if (isResolved) return;
                isResolved = true;
                clearTimeout(gpsTimeout);
                saveLocationToBackend(position.coords.latitude, position.coords.longitude, 'GPS');
            },
            (error) => {
                if (isResolved) return;
                isResolved = true;
                clearTimeout(gpsTimeout);
                fetchIpLocationFallback();
            },
            { enableHighAccuracy: false, timeout: 5000, maximumAge: 0 }
        );
    }, [userId, token, onChange]);

    useEffect(() => {
        if (!mismatch && isLocationSaved) {
            onChange('addressProofDone', true);
        }
    }, [mismatch, onChange, isLocationSaved]);

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!docType) {
            alert('Please select a document type first.');
            e.target.value = '';
            return;
        }

        setUploading(true);
        setUploadProgress(0);
        setUploadFinalizing(false);
        setUploadMsg(`Uploading ${file.name}...`);

        const fd = new FormData();
        fd.append('document', file);
        fd.append('userId', userId);
        fd.append('documentType', `address_proof_${docType.replace(/\s+/g, '_').toLowerCase()}`);

        try {
            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('POST', `${API}/api/documents/upload`);
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);

                xhr.upload.onprogress = (evt) => {
                    if (!evt.lengthComputable) return;
                    const pct = Math.min(100, Math.round((evt.loaded / evt.total) * 100));
                    setUploadProgress(pct);
                    if (pct >= 100) {
                        setUploadFinalizing(true);
                        setUploadMsg(`Upload complete. Finalizing...`);
                    } else {
                        setUploadMsg(`Uploading ${file.name}... ${pct}%`);
                    }
                };

                xhr.onload = () => {
                    try {
                        const parsed = JSON.parse(xhr.responseText || '{}');
                        if (xhr.status >= 200 && xhr.status < 300 && parsed.success) {
                            resolve(parsed);
                        } else {
                            reject(new Error(parsed.message || 'Upload failed'));
                        }
                    } catch {
                        reject(new Error('Upload failed'));
                    }
                };
                xhr.onerror = () => reject(new Error('Network error during upload'));
                xhr.send(fd);
            });

            onChange('addressProofFileName', file.name);
            onChange('addressProofDone', true);
            setUploadMsg(`Successfully uploaded ${file.name}`);
        } catch (error) {
            setUploadMsg(`Upload failed: ${error.message}`);
        } finally {
            setUploading(false);
            setUploadFinalizing(false);
        }
    };

    if (!addressProofStep) return <StepDisabledNotice title="Address Proof" />;

    if (!isLocationSaved) {
        return (
            <div className="alp-step-body">
                <div className="alp-bank-panel" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', background: '#fffbeb', borderColor: '#fde68a', minHeight: '300px' }}>
                    <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📍</div>
                    <h3 style={{ color: '#b45309', margin: '0 0 0.5rem 0' }}>Location Access Required</h3>
                    <p style={{ color: '#92400e', margin: '0 0 1.5rem', fontSize: '1rem', maxWidth: '400px', lineHeight: '1.4' }}>
                        To verify your address automatically, we need access to your device's GPS location. Please allow location permissions to proceed.
                    </p>
                    <button className="alp-btn-primary" onClick={fetchLocation}>
                        Enable Location
                    </button>
                </div>
            </div>
        );
    }

    if (!frontendMatchChecked && isLocationSaved) {
        return (
            <div className="alp-step-body">
                <div className="alp-bank-panel" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', background: '#f8fafc', borderColor: '#e2e8f0', minHeight: '300px' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
                    <h3 style={{ color: '#334155', margin: '0 0 0.5rem 0' }}>Verifying Location...</h3>
                    <p style={{ color: '#64748b', margin: 0, fontSize: '0.95rem', maxWidth: '400px' }}>
                        Please wait while we match your current location with your Aadhaar address.
                    </p>
                </div>
            </div>
        );
    }

    if (!mismatch) {
        return (
            <div className="alp-step-body">
                <div className="alp-bank-panel" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', background: '#f0fdf4', borderColor: '#bbf7d0', minHeight: '300px' }}>
                    <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
                    <h3 style={{ color: '#166534', margin: '0 0 0.5rem 0' }}>Geo-location Matched</h3>
                    <p style={{ color: '#15803d', margin: 0, fontSize: '1rem', maxWidth: '400px' }}>
                        We have automatically verified your address using your device's GPS location. Your current location matches your Aadhaar address. No further proof is required.
                    </p>
                    <div style={{ marginTop: '1.5rem', background: 'rgba(255,255,255,0.7)', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(22, 101, 52, 0.2)', width: '100%', maxWidth: '500px', textAlign: 'left', fontSize: '0.9rem', color: '#166534' }}>
                        <div style={{ marginBottom: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                            <span style={{ fontSize: '1.1rem' }}>🏠</span>
                            <div>
                                <strong>Aadhaar Address:</strong>
                                <div style={{ marginTop: '0.2rem', lineHeight: '1.4' }}>{data.aadhaarAddress || 'N/A'}</div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginTop: '0.75rem' }}>
                            <span style={{ fontSize: '1.1rem' }}>📍</span>
                            <div>
                                <strong>Current Location:</strong>
                                <div style={{ marginTop: '0.2rem' }}>{gpsLocationText || 'Fetching...'}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="alp-step-body">
            <div className="alp-bank-grid">
                <div className="alp-bank-panel">
                    <div className="alp-alert alp-aa-error" style={{ marginTop: '0', padding: '1rem' }}>
                        <strong>Mismatch Detected</strong> <br />
                        <span style={{ fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>Your current GPS location differs from your Aadhaar address. Please provide an alternative address proof.</span>
                        <div style={{ marginTop: '1rem', background: 'rgba(255,255,255,0.7)', padding: '0.75rem', borderRadius: '8px', border: '1px solid rgba(220, 38, 38, 0.2)', textAlign: 'left', fontSize: '0.85rem', color: '#991b1b' }}>
                            <div style={{ marginBottom: '0.5rem' }}><strong>Aadhaar Address:</strong> {data.aadhaarAddress || 'N/A'}</div>
                            <div><strong>Current Location:</strong> {gpsLocationText || 'Fetching...'}</div>
                        </div>
                    </div>

                    <div className="alp-bank-fields" style={{ marginTop: '1.5rem' }}>
                        <div className="alp-field" style={{ gridColumn: '1 / -1' }}>
                            <label>Select Document Type</label>
                            <select
                                className="alp-input"
                                value={docType}
                                onChange={(e) => setDocType(e.target.value)}
                                disabled={uploading}
                            >
                                <option value="">-- Select Proof --</option>
                                <option value="Electricity Bill">Electricity Bill</option>
                                <option value="Rent Agreement">Rent Agreement</option>
                                <option value="Gas Bill">Gas Bill</option>
                                <option value="Water Bill">Water Bill</option>
                            </select>
                        </div>
                    </div>

                    {docType && (
                        <div className="alp-bank-actions" style={{ position: 'relative', marginTop: '1.5rem' }}>
                            <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={handleFileChange}
                                style={{
                                    position: 'absolute', inset: 0, opacity: 0, cursor: uploading ? 'not-allowed' : 'pointer', width: '100%', zIndex: 10
                                }}
                                disabled={uploading}
                            />
                            <button
                                className="alp-btn-ghost"
                                disabled={uploading}
                                style={{ width: '100%', border: '2px dashed #cbd5e1', background: '#f8fafc', padding: '1.5rem', height: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}
                            >
                                <span style={{ fontSize: '2rem' }}>📄</span>
                                <span style={{ fontWeight: 600, color: '#334155' }}>
                                    {uploading ? (uploadFinalizing ? 'Finalizing…' : 'Uploading…') : done ? 'Re-upload Document' : 'Click to Upload File (PDF/Image)'}
                                </span>
                            </button>
                        </div>
                    )}

                    {uploading && (
                        <div className="alp-upload-progress" style={{ marginTop: '1.5rem' }}>
                            <div className="alp-upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
                            <span>{uploadProgress}%</span>
                        </div>
                    )}

                    {uploadMsg && !uploading && (
                        <div style={{ fontSize: '0.875rem', color: done ? '#10b981' : '#ef4444', textAlign: 'center', marginTop: '1rem', fontWeight: 500 }}>
                            {uploadMsg}
                        </div>
                    )}
                </div>

                <div className="alp-bank-panel alp-bank-panel-saved" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', background: done ? '#f8fafc' : '#f1f5f9', borderColor: done ? '#e2e8f0' : '#e2e8f0', minHeight: '300px' }}>
                    {done && fileName ? (
                        <>
                            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📄</div>
                            <h3 style={{ color: '#0f172a', margin: '0 0 0.5rem 0' }}>Document Uploaded</h3>
                            <p style={{ color: '#475569', wordBreak: 'break-all', margin: 0, padding: '0 1rem' }}>{fileName}</p>
                            <div style={{ marginTop: '1.5rem', background: '#d1fae5', color: '#065f46', padding: '0.5rem 1rem', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: 600 }}>✓ Successfully Verified</div>
                        </>
                    ) : (
                        <>
                            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.3 }}>📍</div>
                            <h3 style={{ color: '#475569', margin: '0 0 0.5rem 0' }}>Manual Verification</h3>
                            <p style={{ color: '#64748b', margin: 0, padding: '0 1rem' }}>Please select a document type and upload a clear copy for manual address verification.</p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function StepKycHandoff({ data, onChange }) {
    const [profile, setProfile] = useState(null);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [kycLoading, setKycLoading] = useState(false);
    const [kycError, setKycError] = useState('');
    const [kycInProgress, setKycInProgress] = useState(false);
    const session = JSON.parse(localStorage.getItem('userSession') || '{}');
    const userId = session?.userId;
    const token = session?.token || '';
    const { kycStep } = useFeatureFlags();

    const loadProfile = useCallback(async () => {
        if (!userId) {
            setLoadingProfile(false);
            return;
        }

        try {
            const response = await fetch(`${API}/api/auth/profile/${userId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const json = await response.json();
            if (response.ok && json.success) {
                setProfile(json);
                const kycStatusLocal = String(json.user?.kycStatus || json.user?.kyc_status || 'pending').toLowerCase();
                const panName = json.user?.full_name || '';
                const aadhaarName = json.kycRecords?.aadhaar?.aadhaar_name || '';

                let finalStatus = kycStatusLocal;
                let reason = '';

                // Frontend validation override: STRICT mismatch check has been removed as requested.

                // A bare kyc_status flag isn't proof a real Digilocker pull happened — an
                // account's status can get set to completed/verified without the underlying
                // Aadhaar record ever being populated (e.g. via manual admin tooling). Only
                // trust it once the actual demographic data (UID + name) is present, so KYC
                // can't be treated as done — and the step can't be skipped — without it.
                const aadhaarUidValue = json.kycRecords?.aadhaar?.uid || json.kycRecords?.aadhaar?.aadhaar_uid || '';
                const aadhaarNameValue = json.kycRecords?.aadhaar?.name || json.kycRecords?.aadhaar?.aadhaar_name || '';
                const aadhaarVerified = !!(aadhaarUidValue && aadhaarNameValue);
                if (finalStatus === 'pending' && aadhaarVerified) {
                    finalStatus = 'under_review';
                }

                onChange('kycStatus', finalStatus);
                onChange('kycDone', aadhaarVerified && ['completed', 'verified', 'under_review'].includes(finalStatus));
                onChange('addressMismatch', json.kycRecords?.aadhaar?.addressMismatch || false);
                onChange('aadhaarAddress', json.kycRecords?.aadhaar?.address || '');
                // Used by the declaration video script on the selfie step.
                onChange('applicantName', aadhaarName || panName || '');
                onChange('gpsLatitude', json.user?.lastLatitude || null);
                onChange('gpsLongitude', json.user?.lastLongitude || null);

                if (finalStatus === 'rejected') {
                    if (!reason) {
                        reason = 'KYC Verification Failed. Please ensure your Aadhaar matches your PAN details and retry.';
                        try {
                            const kycDataObj = typeof json.user?.kyc_data === 'string' ? JSON.parse(json.user.kyc_data) : (json.user?.kyc_data || {});
                            if (kycDataObj.mismatchReason) reason = kycDataObj.mismatchReason;
                        } catch (e) { }
                    }
                    setKycError(reason);
                } else if (finalStatus !== 'pending') {
                    setKycError('');
                }
            }
        } catch {
            // Best-effort only.
        } finally {
            setLoadingProfile(false);
        }
    }, [onChange, token, userId]);

    useEffect(() => {
        loadProfile();
    }, [loadProfile]);

    useEffect(() => {
        if (!kycStep && !data.kycDone) onChange('kycDone', true);
    }, [kycStep, data.kycDone, onChange]);

    const user = profile?.user || {};
    const records = profile?.kycRecords || {};
    const aadhaarRecord = records.aadhaar || {};
    // A status flag alone (kyc_status, or the aadhaar record's own status column) isn't
    // proof of a real Digilocker pull — require the actual UID + name to be present so
    // this panel can't show "Verified" for an account whose status was set without data.
    const aadhaarHasData = !!((aadhaarRecord.uid || aadhaarRecord.aadhaar_uid) && (aadhaarRecord.name || aadhaarRecord.aadhaar_name));
    const kycDone = aadhaarHasData && ['completed', 'verified', 'under_review'].includes(String(user.kycStatus || user.kyc_status || '').toLowerCase());

    // Poll while KYC is in progress
    useEffect(() => {
        if (!kycInProgress || kycDone) return;
        const iv = setInterval(() => {
            loadProfile();
        }, 5000);
        return () => clearInterval(iv);
    }, [kycInProgress, kycDone, loadProfile]);
    // Auto-stop polling when kyc is done
    useEffect(() => {
        if (kycDone && kycInProgress) {
            setKycInProgress(false);
        }
    }, [kycDone, kycInProgress]);

    const displayName = user.name || aadhaarRecord.name || session?.name || 'Applicant';
    const aadhaarUid = String(
        aadhaarRecord.uid ||
        aadhaarRecord.aadhaar_uid ||
        ''
    ).trim();
    const aadhaarDob = String(
        aadhaarRecord.dob ||
        aadhaarRecord.aadhaar_dob ||
        user.dob ||
        ''
    ).trim();
    const aadhaarGender = String(
        aadhaarRecord.gender ||
        aadhaarRecord.aadhaar_gender ||
        user.gender ||
        ''
    ).trim();
    const aadhaarAddress = String(
        aadhaarRecord.address ||
        aadhaarRecord.aadhaar_address ||
        user.address ||
        ''
    ).trim();
    const aadhaarName = String(
        aadhaarRecord.name ||
        aadhaarRecord.aadhaar_name ||
        displayName ||
        ''
    ).trim();
    const aadhaarRows = [
        { label: 'Name', value: aadhaarName || 'Pending' },
        { label: 'UID', value: aadhaarUid || 'Pending' },
        { label: 'DOB', value: aadhaarDob || 'Pending' },
        { label: 'Gender', value: aadhaarGender || 'Pending' },
        { label: 'Address', value: aadhaarAddress || 'Pending' },
    ];

    const startDigilocker = useCallback(async () => {
        setKycError('');
        setKycLoading(true);

        const width = 500;
        const height = 750;
        const left = (window.screen.width / 2) - (width / 2);
        const top = (window.screen.height / 2) - (height / 2);

        const popup = window.open(
            'about:blank',
            'DigilockerKYC',
            `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes,status=no`
        );
        if (popup) {
            popup.document.write('<h3>Loading Digilocker securely... Please wait.</h3>');
        }

        try {
            const response = await fetch(`${API}/api/kyc/initiate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({ userId })
            });
            const json = await response.json();
            if (!response.ok || !json.success || !json.url) {
                throw new Error(json.message || 'Failed to initiate Digilocker');
            }

            if (popup) {
                popup.location.href = json.url;
            } else {
                window.location.href = json.url;
            }
            setKycInProgress(true);
        } catch (err) {
            setKycError(err.message || 'Unable to open Digilocker');
            if (popup) popup.close();
        } finally {
            setKycLoading(false);
        }
    }, [token, userId]);

    if (!kycStep) return <StepDisabledNotice title="Digilocker KYC" />;

    return (
        <div className="alp-step-body">
            <div className="alp-aa-card" style={{ padding: '1.75rem' }}>
                <div className="alp-aa-choices-grid">
                    <div className="alp-aa-choice-card recommended alp-kyc-overview-card">
                        <div className="alp-aa-choice-badge">{kycDone ? 'KYC Verified' : 'Digilocker • Aadhaar'}</div>
                        <div className="alp-aa-choice-icon">📜</div>
                        <h4>Digilocker KYC</h4>
                        <p>{kycDone ? 'Aadhaar verification completed successfully.' : 'Complete Aadhaar verification on the dedicated KYC page.'}</p>

                        <div className="alp-kyc-quick-stats">
                            <div className="alp-kyc-stat-chip">
                                <span>Status</span>
                                <strong>{kycDone ? 'Verified' : kycInProgress ? 'Verifying' : loadingProfile ? 'Loading' : 'Pending'}</strong>
                            </div>
                            <div className="alp-kyc-stat-chip">
                                <span>Source</span>
                                <strong>Digilocker</strong>
                            </div>
                        </div>

                        <div className="alp-aa-choice-actions">
                            <button
                                className="alp-btn-primary"
                                onClick={startDigilocker}
                                disabled={kycLoading || kycDone}
                                style={{ width: '100%' }}
                            >
                                {kycDone ? 'Digilocker KYC Completed' : (kycLoading ? 'Opening Digilocker...' : (kycInProgress ? 'Waiting for verification...' : (kycError ? 'Retry Digilocker KYC' : 'Open Digilocker KYC')))}
                            </button>
                            {kycInProgress && !kycDone && !kycLoading && (
                                <p style={{ margin: '0.6rem 0 0', fontSize: '0.8rem', color: '#64748b', lineHeight: 1.4 }}>
                                    Complete the Aadhaar consent in the Digilocker window — we're checking automatically every few seconds and this page will update on its own.
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="alp-aa-choice-card alt">
                        <div className="alp-aa-choice-badge alt">{kycDone ? 'Verified' : 'Pending'}</div>
                        <div className="alp-aa-choice-icon alt">🆔</div>
                        <h4>Aadhaar Details</h4>
                        <p>{kycDone ? 'Verified Aadhaar data fetched from Digilocker.' : 'Once Digilocker verifies the client, the Aadhaar details will appear here.'}</p>

                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', marginBottom: '0.9rem' }}>
                            <div>
                                <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#a5761e' }}>Aadhaar Details</div>
                                <div style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', marginTop: '0.25rem' }}>{kycDone ? 'Verified Aadhaar' : 'Waiting for KYC'}</div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gap: '0.4rem' }}>
                            {kycDone ? aadhaarRows.map(row => (
                                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', padding: '0.45rem 0.65rem', borderRadius: '10px', background: '#fff', border: '1px solid #eef2f7' }}>
                                    <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700 }}>{row.label}</span>
                                    <strong style={{ color: '#0f172a', fontSize: '0.8rem', textAlign: 'right', wordBreak: 'break-word', maxWidth: '65%' }}>{row.value}</strong>
                                </div>
                            )) : (
                                <div style={{ padding: '0.9rem 1rem', borderRadius: '14px', background: '#fff', border: '1px solid #eef2f7', color: '#64748b', fontSize: '0.92rem', lineHeight: 1.5 }}>
                                    Once Digilocker verifies the client, the Aadhaar details will appear here.
                                </div>
                            )}
                        </div>
                        {kycError && (
                            <div style={{ marginTop: '0.85rem', color: '#b91c1c', fontSize: '0.86rem', fontWeight: 600 }}>
                                {kycError}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}


function getEmploymentValidationMessage(data) {
    const activeType = EMP_TYPES.includes(data.empType) ? data.empType : '';
    if (!activeType) {
        return 'Please select one income profile to continue.';
    }

    const fields = EMPLOYMENT_FIELDS[activeType] || [];
    const missingField = fields.find((field) => field.required !== false && !String(data[field.key] || '').trim());
    if (missingField) {
        return `Please fill ${missingField.label}.`;
    }

    const incomeValue = Number(data.income || 0);
    if (!Number.isFinite(incomeValue) || incomeValue <= 0) {
        return 'Please enter a valid monthly income.';
    }

    const pEmailValue = String(data.personal_email || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pEmailValue)) {
        return 'Please enter a valid personal email address.';
    }

    const emailValue = String(data.email || '').trim();
    if (emailValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
        return 'Please enter a valid company email address.';
    }

    for (const { n } of REFERENCE_SLOTS) {
        if (!String(data[`ref${n}_name`] || '').trim()) return `Please enter Reference ${n} name.`;
        if (!/^[6-9]\d{9}$/.test(String(data[`ref${n}_phone`] || '').replace(/\D/g, ''))) {
            return `Please enter a valid 10-digit mobile number for Reference ${n}.`;
        }
        const relation = data[`ref${n}_relation`];
        if (!REFERENCE_RELATIONS.includes(relation)) return `Please select the relation for Reference ${n}.`;
        if (relation === 'Other' && !CUSTOM_RELATION_RE.test(String(data[`ref${n}_relation_other`] || '').trim())) {
            return `Please specify the relation for Reference ${n}.`;
        }
    }

    return '';
}

function StepEmployment({ data, onChange, validationMessage = '', isFastTrackReloan = false }) {
    const activeType = EMP_TYPES.includes(data.empType) ? data.empType : '';
    const fields = EMPLOYMENT_FIELDS[activeType] || [];
    const activeOption = activeType ? EMPLOYMENT_OPTIONS[activeType] : null;
    // Catches the common typo of dropping the last digit (or two) of a reference's
    // mobile number: nudge with a popup only when it's close (8-9 digits), not on
    // every keystroke — a 1-7 digit entry is just "still typing".
    const [refPhoneWarning, setRefPhoneWarning] = useState(null);
    const handleRefPhoneBlur = (n) => {
        const digits = String(data[`ref${n}_phone`] || '').replace(/\D/g, '');
        if (digits.length === 8 || digits.length === 9) {
            setRefPhoneWarning({ n, digits: digits.length });
        }
    };

    // A repeat customer whose last loan already closed doesn't need to redo this —
    // we don't durably store employer/income/reference details anywhere to prefill
    // them with, so instead of a stale/blank form we just skip straight past it.
    // "My details have changed" reveals the real form for the rare person who needs
    // it; stored on `data` (not local state) so the parent's Continue-button gate
    // knows to stop bypassing validation once they're actually editing again.
    if (isFastTrackReloan && !data.employmentFastTrackEditing) {
        return (
            <div className="alp-step-body">
                <div className="alp-employment-shell">
                    <div className="alp-emp-choose">
                        <div className="alp-employment-intro">
                            <h3>Using your details from last time</h3>
                            <p>We already have your employment and reference details on file — no need to fill this in again.</p>
                        </div>
                        <button
                            type="button"
                            className="alp-pill alp-pill-cyan"
                            onClick={() => onChange('employmentFastTrackEditing', true)}
                        >
                            <span className="alp-pill-icon">✏️</span>
                            <span className="alp-pill-label">My details have changed</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="alp-step-body">
            <div className="alp-employment-shell">
                {!activeType && (
                    <div className="alp-emp-choose">
                        <div className="alp-employment-intro">
                            <h3>How do you earn?</h3>
                            <p>Pick one to continue.</p>
                        </div>

                        <div className="alp-pills">
                            {EMP_TYPES.map(t => {
                                const option = EMPLOYMENT_OPTIONS[t];
                                return (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => onChange('empType', t)}
                                        className={`alp-pill alp-pill-${option.tone}`}
                                    >
                                        <span className="alp-pill-icon">{option.icon}</span>
                                        <span className="alp-pill-label">{option.label}</span>
                                        <span className="alp-pill-desc">{option.desc}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}


                {validationMessage && (
                    <div className="alp-alert alp-aa-error" style={{ marginTop: '1rem' }}>
                        <strong>Employment Info</strong> {validationMessage}
                    </div>
                )}

                {activeType && (
                    <div className="alp-employment-focus">
                        <button
                            type="button"
                            className={`alp-pill alp-pill-${activeOption.tone} active alp-pill-selected`}
                            onClick={() => onChange('empType', '')}
                        >
                            <span className="alp-pill-icon">{activeOption.icon}</span>
                            <span className="alp-pill-label">{activeOption.label}</span>
                            <span className="alp-pill-change">Change</span>
                        </button>

                        <div className="alp-employment-card">
                            <div className="alp-fields-grid">
                                {fields.map((f) => (
                                    <div className="alp-field" key={f.key}>
                                        <label>
                                            <span className="alp-field-icon">{f.icon}</span>
                                            {f.label} {f.required !== false && '*'}
                                        </label>
                                        <input
                                            type={f.type || 'text'}
                                            placeholder={f.placeholder}
                                            value={data[f.key] || ''}
                                            onChange={(e) => onChange(f.key, e.target.value)}
                                        />
                                    </div>
                                ))}
                            </div>

                            <div className="alp-references-section">
                                <div className="alp-references-heading">
                                    <span>👥</span> References
                                </div>
                                {REFERENCE_SLOTS.map(({ n, namePlaceholder, phonePlaceholder }) => (
                                    <div className="alp-ref-block" key={n}>
                                        <div className="alp-ref-caption">Reference {n}</div>
                                        <div className="alp-ref-fields">
                                            <div className="alp-field">
                                                <label><span className="alp-field-icon">👤</span> Name *</label>
                                                <input
                                                    type="text"
                                                    placeholder={namePlaceholder}
                                                    value={data[`ref${n}_name`] || ''}
                                                    onChange={(e) => onChange(`ref${n}_name`, e.target.value)}
                                                />
                                            </div>
                                            <div className="alp-field">
                                                <label><span className="alp-field-icon">📞</span> Mobile *</label>
                                                <input
                                                    type="tel"
                                                    placeholder={phonePlaceholder}
                                                    maxLength={10}
                                                    value={data[`ref${n}_phone`] || ''}
                                                    onChange={(e) => onChange(`ref${n}_phone`, e.target.value.replace(/\D/g, '').slice(0, 10))}
                                                    onBlur={() => handleRefPhoneBlur(n)}
                                                />
                                            </div>
                                            <div className="alp-field">
                                                <label><span className="alp-field-icon">🤝</span> Relation *</label>
                                                <select
                                                    value={data[`ref${n}_relation`] || ''}
                                                    onChange={(e) => {
                                                        onChange(`ref${n}_relation`, e.target.value);
                                                        if (e.target.value !== 'Other') onChange(`ref${n}_relation_other`, '');
                                                    }}
                                                >
                                                    <option value="">Select relation</option>
                                                    {REFERENCE_RELATIONS.map(r => (
                                                        <option key={r} value={r}>{r}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            {data[`ref${n}_relation`] === 'Other' && (
                                                <div className="alp-field alp-ref-other">
                                                    <label><span className="alp-field-icon">✍️</span> Specify Relation *</label>
                                                    <input
                                                        type="text"
                                                        placeholder="e.g. Neighbour"
                                                        maxLength={50}
                                                        value={data[`ref${n}_relation_other`] || ''}
                                                        onChange={(e) => onChange(`ref${n}_relation_other`, e.target.value)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {refPhoneWarning && (
                <div className="alp-aa-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.7)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
                    <div className="alp-aa-modal-content" style={{ width: '90%', maxWidth: '400px', background: 'white', borderRadius: '16px', padding: '1.5rem', textAlign: 'center', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                        <h3 style={{ color: '#0f172a', marginBottom: '0.5rem' }}>Check Reference {refPhoneWarning.n}'s number</h3>
                        <p style={{ color: '#475569', fontSize: '0.95rem', marginBottom: '1.5rem', lineHeight: '1.5' }}>
                            You entered only {refPhoneWarning.digits} digits. A valid mobile number needs exactly 10 digits — please double-check and complete it.
                        </p>
                        <button className="alp-btn-primary" onClick={() => setRefPhoneWarning(null)} style={{ width: '100%' }}>
                            Okay, let me fix it
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function StepReview() {
    // Clean submit screen — no eligibility assessment or applicant details are shown here.
    // The client simply confirms and submits; the footer "Submit" button runs handleFinalSubmit.
    return (
        <div className="alp-step-body">
            <div className="alp-review-section" style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                <div style={{ fontSize: '2.75rem', marginBottom: '0.5rem' }}>🚀</div>
                <div className="alp-review-section-title" style={{ justifyContent: 'center' }}>Ready to Submit</div>
                <p style={{ color: '#475569', fontSize: '0.95rem', marginTop: '0.5rem', maxWidth: '460px', marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
                    You've completed all the required steps. Tap <strong>Submit</strong> below to send your loan application. We'll review it and share the final loan amount and terms with you shortly.
                </p>
            </div>
        </div>
    );
}

export default function ApplyLoanPage() {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [done, setDone] = useState([]);
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [aaDataPendingError, setAaDataPendingError] = useState(false);
    const [stepError, setStepError] = useState('');
    const [returnToFinal, setReturnToFinal] = useState(false);
    const [data, setData] = useState({ amount: 50000, tenure: '14' });

    const featureFlags = useFeatureFlags();
    const isStepEnabled = useCallback((index) => {
        const key = STEP_FLAG_KEYS[index];
        return key ? featureFlags[key] !== false : true;
    }, [featureFlags]);
    // Walks from `from` in `direction` (+1/-1) until it lands on an enabled step, so a
    // disabled step is never shown to the applicant — Continue/Back just glide past it.
    const nextEnabledStep = useCallback((from, direction) => {
        let idx = Math.max(0, Math.min(STEPS.length - 1, from));
        while (idx > 0 && idx < STEPS.length - 1 && !isStepEnabled(idx)) {
            idx += direction;
        }
        return Math.max(0, Math.min(STEPS.length - 1, idx));
    }, [isStepEnabled]);
    // Safety net: if the current step is ever disabled (e.g. flags arrive after the saved
    // stage was restored), correct it immediately instead of rendering that step's body.
    useEffect(() => {
        if (!isStepEnabled(step)) {
            setStep(nextEnabledStep(step, 1));
        }
    }, [step, isStepEnabled, nextEnabledStep]);

    useEffect(() => {
        if (aaDataPendingError && String(data.aaSession?.fi_data_status || '').toUpperCase() === 'FETCHED') {
            setAaDataPendingError(false);
        }
    }, [aaDataPendingError, data.aaSession?.fi_data_status]);
    const breakdown = getLoanBreakdown(data.amount, data.tenure);
    const [journeyReady, setJourneyReady] = useState(false);
    const session = JSON.parse(localStorage.getItem('userSession') || '{}');
    const userId = session?.userId;
    const token = session?.token || '';
    const journeyHydratedRef = useRef(false);
    // Set when the saved journey could not be loaded, so the blank initial state is not autosaved over it.
    const journeyLoadFailedRef = useRef(false);
    const sessionEmail = session?.email || '';
    const alreadySubmittedError = /already have an active or pending loan/i.test(String(submitError || ''));

    const [globalEligibility, setGlobalEligibility] = useState(null);

    useEffect(() => {
        if (!userId) return;
        const fetchGlobalEligibility = async () => {
            try {
                const response = await fetch(`${API}/api/loans/eligibility/${userId}?_t=${Date.now()}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined
                });
                const result = await response.json();
                if (result.success && result.eligibility) {
                    setGlobalEligibility(result.eligibility);
                    const eligibleAmt = Number(result.eligibility.finalApprovedAmount || 0);
                    const isElig = result.eligibility.isEligible;
                    // If eligible and has approved amount, use it; if not eligible set 0
                    if (isElig && eligibleAmt > 0) {
                        setData(prev => ({ ...prev, amount: eligibleAmt }));
                    } else if (!isElig) {
                        setData(prev => ({ ...prev, amount: 0 }));
                    }
                }
            } catch (err) {
                console.error("Error fetching global eligibility:", err);
            }
        };
        fetchGlobalEligibility();
    }, [userId, token]);

    // isFastTrack: a repeat customer whose last loan closed successfully. Their
    // Employment/Bank/Address Proof steps get pre-satisfied from durable data
    // instead of redone — see StepEmployment, StepBankVerification and the
    // addressProofStep override below. Stored on `data` (not a separate
    // useState) so it rides along for free on the existing draft
    // autosave/localStorage/restore machinery and survives a page refresh.
    const resetJourneyForNewApplication = useCallback((isFastTrack = false) => {
        journeyHydratedRef.current = true;
        const defaultAmt = globalEligibility?.finalApprovedAmount && Number(globalEligibility.finalApprovedAmount) > 0
            ? Number(globalEligibility.finalApprovedAmount)
            : 50000;
        setData({
            amount: defaultAmt, tenure: '14',
            ...(sessionEmail ? { email: sessionEmail } : {}),
            ...(isFastTrack ? { isFastTrackReloan: true } : {})
        });
        setStep(0);
        setDone([]);
        setSubmitted(false);
        setSubmitError('');
    }, [sessionEmail, globalEligibility]);

    useEffect(() => {
        if (!userId) return;
        fetch(`${API}/api/dashboard/all-data/${userId}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined
        })
            .then(res => res.json())
            .then(data => {
                if (data.success && data.stats) {
                    if (Number(data.stats.activeLoans || 0) > 0 || Number(data.stats.pendingLoans || 0) > 0) {
                        setSubmitted(true);
                    } else {
                        setSubmitted(false);
                    }
                }
            })
            .catch(console.error);
    }, [userId, token]);

    useEffect(() => {
        if (!sessionEmail) return;
        setData((prev) => (prev.email ? prev : { ...prev, email: sessionEmail }));
    }, [sessionEmail]);

    useEffect(() => {
        let active = true;

        const loadJourneyData = async () => {
            if (!userId) {
                if (active) setJourneyReady(true);
                return;
            }

            try {
                const response = await fetch(`${API}/api/loan-journey/data`, {
                    credentials: 'include',
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined
                });
                const json = await response.json().catch(() => ({}));

                if (!active) return;
                if (!response.ok || !json.success) {
                    journeyLoadFailedRef.current = true;
                    return;
                }

                // If there's no journey record at all, mark as hydrated so they can start saving
                if (!json.record) {
                    journeyHydratedRef.current = true;
                    return;
                }

                const latestLoanStatus = json.latestLoan?.status || '';
                const savedJourneyStatus = String(json.record.status || '').toUpperCase();
                const savedCurrentStage = Number(json.record.currentStage || 1);
                // currentStage is 1-based (step + 1), so sitting on the final Review step
                // saves STEPS.length — that is not a submission.
                // We only wipe if they go beyond STEPS.length or it is explicitly marked COMPLETED.
                const hasSavedSubmitStage = savedCurrentStage > STEPS.length || savedJourneyStatus === 'COMPLETED';

                // If user has an active/pending loan, IMMEDIATELY show them the submitted screen!
                if (json.latestLoan && isBlockingLoanStatus(latestLoanStatus)) {
                    setSubmitted(true);
                    if (active) setJourneyReady(true);
                    return;
                }

                // If user has an old non-blocking loan AND their current draft is already completed, wipe it.
                // A loan that closed (not rejected) makes this a repeat customer we've already
                // fully verified — fast-track them past Employment/Bank/Address Proof instead of
                // redoing the whole journey (Account Aggregator, Selfie and Declaration Video stay
                // mandatory regardless).
                if (json.latestLoan && !isBlockingLoanStatus(latestLoanStatus) && hasSavedSubmitStage) {
                    const normalizedLatestStatus = String(latestLoanStatus).trim().toLowerCase();
                    const isFastTrack = normalizedLatestStatus === 'closed' || normalizedLatestStatus === 'completed';
                    resetJourneyForNewApplication(isFastTrack);
                    fetch(`${API}/api/loan-progress/clear`, {
                        method: 'POST',
                        credentials: 'include',
                        headers: token ? { Authorization: `Bearer ${token}` } : undefined
                    }).catch(() => { });
                    return;
                }

                // If user has NO latest loan BUT their current draft is completed, wipe it
                if (!json.latestLoan && hasSavedSubmitStage) {
                    resetJourneyForNewApplication();
                    localStorage.removeItem(`loan_journey_draft_${userId}`);
                    fetch(`${API}/api/loan-progress/clear`, {
                        method: 'POST',
                        credentials: 'include',
                        headers: token ? { Authorization: `Bearer ${token}` } : undefined
                    }).catch(() => { });
                    return;
                }

                const backendData = json.record.journeyData && typeof json.record.journeyData === 'object'
                    ? json.record.journeyData
                    : {};

                const localDraftData = JSON.parse(localStorage.getItem(`loan_journey_draft_${userId}`) || '{}');

                // Merge backend data with local draft, preferring local draft if it has more recent unsaved changes
                const savedData = { ...backendData, ...localDraftData };

                // KYC is the only step with a live server-side recomputation (loadProfile),
                // so its saved flags are dropped here and replaced below with that fresher
                // source. The other steps' *Done flags don't have an equivalent live check —
                // restoring them normally (like every other saved field) is what lets a
                // completed step stay completed across a reload instead of forcing a redo.
                delete savedData.kycDone;
                delete savedData.kycStatus;

                // Restore KYC flags from backend's live computation (top-level fields)
                if (json.kycDone !== undefined) savedData.kycDone = json.kycDone;
                if (json.kycStatus !== undefined) savedData.kycStatus = json.kycStatus;

                const savedStage = Math.max(1, Math.min(STEPS.length, savedCurrentStage));

                journeyHydratedRef.current = true;
                setData((prev) => ({ ...prev, ...savedData }));
                setStep(nextEnabledStep(savedStage - 1, 1));
                setDone(STEPS.slice(0, Math.max(0, savedStage - 1)).map((item) => item.id));
            } catch (error) {
                journeyLoadFailedRef.current = true;
                console.error('Failed to load journey data:', error);
            } finally {
                if (active) setJourneyReady(true);
            }
        };

        loadJourneyData();
        return () => {
            active = false;
        };
        // nextEnabledStep is intentionally excluded: it changes identity once feature
        // flags finish loading, and this effect must run only once per user/token to
        // hydrate the journey — not re-fetch and reset `step`/`data` a second time.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetJourneyForNewApplication, token, userId]);

    // On mount: fetch profile to set kycDone even when StepKycHandoff is not mounted
    useEffect(() => {
        if (!userId) return;
        let active = true;
        (async () => {
            try {
                const response = await fetch(`${API}/api/auth/profile/${userId}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined
                });
                const json = await response.json();
                if (!active || !response.ok || !json.success) return;
                const kycStatusLocal = String(json.user?.kycStatus || json.user?.kyc_status || 'pending').toLowerCase();
                const aadhaarRecord = json.kycRecords?.aadhaar || {};
                // Same rule as StepKycHandoff: a status flag alone isn't proof of a real
                // Digilocker pull — require the actual UID + name before treating KYC as done.
                const aadhaarVerified = !!((aadhaarRecord.uid || aadhaarRecord.aadhaar_uid) && (aadhaarRecord.name || aadhaarRecord.aadhaar_name));
                let finalStatus = kycStatusLocal;
                if (finalStatus === 'pending' && aadhaarVerified) {
                    finalStatus = 'under_review';
                }
                const isDone = aadhaarVerified && ['completed', 'verified', 'under_review'].includes(finalStatus);
                setData(prev => ({ ...prev, kycDone: isDone, kycStatus: finalStatus }));
            } catch {
                // best-effort only
            }
        })();
        return () => { active = false; };
    }, [userId, token]);

    const cur = STEPS[step];
    const accentRgb = hexToRgb(cur.color);
    const employmentValidationMessage = getEmploymentValidationMessage(data);
    // Fast-track reloans skip requiring fresh employment input (see StepEmployment) —
    // both the Continue-button gate and next() below key off this one flag. Once they
    // opt into "My details have changed" the real form (and its validation) applies again.
    const isEmploymentStepValid = !employmentValidationMessage
        || (!!data.isFastTrackReloan && !data.employmentFastTrackEditing);
    const set = useCallback((k, v) => {
        const invalidatingKeys = new Set([
            'amount', 'tenure', 'empType', 'employer', 'income',
            'designation', 'businessType', 'industry', 'workType'
        ]);

        setData(p => {
            const next = { ...p, [k]: v };
            if (invalidatingKeys.has(k) && p.aaMethod !== 'manual_upload' && !p.aaManualDone) {
                next.aaStatus = 'idle';
                next.aaDone = false;
                next.aaSession = null;
            }
            if (invalidatingKeys.has(k)) {
                next.selfieDone = false;
                next.selfieImage = '';
                next.selfieCapturedAt = null;
                next.selfieUploaded = false;
                // The declaration script reads out the amount and tenure, so a
                // recording made against the old figures is no longer valid.
                next.declarationVideoDone = false;
                next.declarationVideoDocId = null;
                next.declarationVideoRecordedAt = null;
                next.bankDone = false;
                next.bankAccountNumber = '';
                next.bankIfscCode = '';
                next.esignDone = false;
                next.borrowerSignature = '';
                next.esignSignedAt = null;
            }
            return next;
        });
        setStepError('');
    }, []);

    useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [step]);

    const aaFiDataFetched = String(data.aaSession?.fi_data_status || '').toUpperCase() === 'FETCHED';
    const aaConsentApprovedOrFetched = aaFiDataFetched || (() => {
        const cs = String(data.aaSession?.consent_status || '').toUpperCase();
        return cs === 'ACTIVE' || cs === 'CONSENT_APPROVED' || cs === 'COMPLETED' || cs === 'SUCCESS';
    })();

    useEffect(() => {
        if (returnToFinal && (aaFiDataFetched || data.aaDone)) {
            setReturnToFinal(false);
            setStep(STEPS.length - 1);
        }
    }, [returnToFinal, aaFiDataFetched, data.aaDone]);

    useEffect(() => {
        if (!userId || !journeyReady) return undefined;

        // Synchronously save to local storage
        localStorage.setItem(`loan_journey_draft_${userId}`, JSON.stringify(data));

        const timer = window.setTimeout(async () => {
            if (!journeyReady || !userId || submitted || journeyLoadFailedRef.current) return;
            try {
                await fetch(`${API}/api/loan-progress/save`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {})
                    },
                    body: JSON.stringify({
                        currentStage: step + 1,
                        status: submitted ? 'COMPLETED' : 'IN_PROGRESS',
                        journeyData: data
                    })
                });
            } catch (error) {
                console.error('Failed to auto-save journey:', error);
            } finally {
                journeyHydratedRef.current = true;
            }
        }, journeyHydratedRef.current ? 700 : 0);

        return () => window.clearTimeout(timer);
    }, [data, journeyReady, step, submitted, token, userId]);

    const next = () => {
        if (step === 0 && !isEmploymentStepValid) {
            setStepError(employmentValidationMessage);
            return;
        }

        if (step === 1 && !data.kycDone) {
            setStepError('Please complete your Aadhaar KYC verification before proceeding.');
            return;
        }

        if (step === 2 && !data.aaDone) {
            setStepError('Please connect your bank account or upload a statement to proceed.');
            return;
        }

        if (step === 3 && !data.bankDone) {
            setStepError('Please verify your bank account to proceed.');
            return;
        }

        if (step === 4 && !data.selfieDone) {
            setStepError('Please complete the selfie verification to proceed.');
            return;
        }

        if (step === 5 && !data.declarationVideoDone) {
            setStepError('Please record and submit your declaration video to proceed.');
            return;
        }

        if (step === 6 && !data.addressProofDone) {
            setStepError('Please upload your address proof to proceed.');
            return;
        }

        setStepError('');
        if (step < STEPS.length - 1) {
            setDone(p => [...new Set([...p, cur.id])]);
            setStep(p => nextEnabledStep(p + 1, 1));
        } else {
            setSubmitted(true);
        }
    };

    const handleFinalSubmit = useCallback(async () => {
        if (!userId) {
            setSubmitError('Session expired. Please login again.');
            return;
        }

        if (data.aaMethod === 'account_aggregator') {
            const aaConsentStatus = String(data.aaSession?.consent_status || '').toUpperCase();
            const aaFiStatus = String(data.aaSession?.fi_data_status || '').toUpperCase();
            const consentApproved = aaConsentStatus === 'ACTIVE' || aaConsentStatus === 'CONSENT_APPROVED' || aaConsentStatus === 'COMPLETED' || aaConsentStatus === 'SUCCESS';

            if (!consentApproved && aaFiStatus !== 'FETCHED') {
                setSubmitError('Please complete the Account Aggregator consent process before submitting.');
                return;
            }

            if (consentApproved && aaFiStatus !== 'FETCHED') {
                setAaDataPendingError(true);
                return;
            }
        }

        setIsSubmitting(true);
        setSubmitError('');
        setAaDataPendingError(false);

        try {
            const response = await fetch(`${API}/api/loans/apply`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    type: data.purpose || 'Personal Loan',
                    amount: Number(data.amount || 0),
                    tenure: Number(data.tenure || 14),
                    emi: breakdown.totalRepayment
                })
            });

            const json = await response.json().catch(() => ({}));
            if (!response.ok || !json.success) {
                throw new Error(json.message || 'Failed to submit application');
            }

            setData(prev => ({
                ...prev,
                loanId: json.loanId || prev.loanId,
                loanApplied: true
            }));

            await fetch(`${API}/api/loan-progress/save`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    currentStage: 10,
                    status: 'COMPLETED',
                    loanId: json.loanId || null,
                    journeyData: {
                        ...data,
                        loanId: json.loanId || null,
                        loanApplied: true
                    }
                })
            });

            setDone(prev => (prev.includes('review') ? prev : [...prev, 'review']));
            setSubmitted(true);
        } catch (err) {
            setSubmitError(err.message || 'Failed to complete journey');
        } finally {
            setIsSubmitting(false);
        }
    }, [breakdown.totalRepayment, data, token, userId]);

    if (submitted) return (
        <div className="alp-root alp-compact" style={{ '--alp-accent': cur.color, '--alp-accent-rgb': accentRgb }}>
            <div className="alp-success-screen">
                <div className="alp-success-box">
                    <div className="alp-success-burst">🎉</div>
                    <h1>Journey Completed</h1>
                    <p>Your loan has been recorded. You can review it in My Loans anytime.</p>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <button className="alp-btn-ghost" onClick={() => navigate('/dashboard/loans')}>View My Loans</button>
                        <button className="alp-btn-ghost" onClick={() => navigate('/dashboard')}>Dashboard</button>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className={`alp-root alp-compact ${step === STEPS.length - 1 ? 'alp-submit-view' : ''}`} style={{ '--alp-accent': cur.color, '--alp-accent-rgb': accentRgb }}>
            <section className="alp-journey-card" aria-label="Journey map">
                <h3>Journey Map</h3>
                <div className="alp-journey-rail">
                    {STEPS.map((s) => {
                        const isDone = done.includes(s.id);
                        const isActive = s.id === step;
                        return (
                            <div
                                key={s.id}
                                className={`alp-journey-step ${isActive ? 'active' : isDone ? 'done' : ''}`}
                                style={{
                                    '--alp-step-color': s.color,
                                    '--alp-step-color-soft': `${s.color}18`,
                                }}
                            >
                                <div className="alp-journey-node">{s.icon}</div>
                                <div className="alp-journey-title">{s.title}</div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <main className="alp-main">
                <div className={`alp-card ${step === STEPS.length - 1 ? 'alp-review-card' : ''}`} style={{ '--alp-accent': cur.color, '--alp-accent-rgb': accentRgb }}>
                    {step !== 0 && (
                        <div className={`alp-premium-header ${step === 2 ? 'alp-aa-stage-header' : step === 3 ? 'alp-bank-stage-header' : step === 4 ? 'alp-selfie-stage-header' : step === 5 ? 'alp-decl-stage-header' : step === 6 ? 'alp-address-stage-header' : step === STEPS.length - 1 ? 'alp-review-stage-header' : ''}`}>
                            <div className="alp-premium-icon">{cur.icon}</div>
                            {step >= 2 && step <= 6 && <div className="alp-premium-kicker">Stage {step + 1}</div>}
                            <div className="alp-premium-copy">
                                <h2>{cur.title}</h2>
                                <p>{cur.desc}</p>
                            </div>
                        </div>
                    )}

                    {step === 0 && <StepEmployment data={data} onChange={set} validationMessage={stepError} isFastTrackReloan={!!data.isFastTrackReloan} />}
                    {step === 1 && <StepKycHandoff data={data} onChange={set} />}
                    {step === 2 && <StepAccountAggregator data={data} onChange={set} />}
                    {step === 3 && <StepBankVerification data={data} onChange={set} />}
                    {step === 4 && <StepSelfieCapture data={data} onChange={set} />}
                    {step === 5 && <StepDeclarationVideo data={data} onChange={set} />}
                    {step === 6 && <StepAddressProof data={data} onChange={set} />}
                    {step === 7 && (
                        <>
                            {data.aaMethod === 'account_aggregator' && !aaFiDataFetched && (
                                <div className="alp-alert alp-alert-warning" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexDirection: 'column' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <span style={{ fontSize: '1.1rem' }}>⚠️</span>
                                        <div>
                                            <strong style={{ display: 'block' }}>
                                                {aaConsentApprovedOrFetched ? 'Fetching Bank Data…' : 'Bank Data Not Received'}
                                            </strong>
                                            <span style={{ fontSize: '0.85rem', opacity: 0.85 }}>
                                                {aaConsentApprovedOrFetched
                                                    ? 'Consent approved. Data is being fetched in background — you can submit or wait for it to complete.'
                                                    : 'Account Aggregator verification is pending. Complete it to strengthen your application.'}
                                            </span>
                                        </div>
                                    </div>
                                    {!aaConsentApprovedOrFetched && (
                                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                            <button
                                                className="alp-btn-primary"
                                                style={{ alignSelf: 'flex-start', fontSize: '0.875rem', padding: '0.45rem 1rem' }}
                                                onClick={() => { setReturnToFinal(true); setStep(nextEnabledStep(2, 1)); }}
                                            >
                                                Complete AA Verification
                                            </button>
                                            <button
                                                className="alp-btn-ghost"
                                                style={{ alignSelf: 'flex-start', fontSize: '0.875rem', padding: '0.45rem 1rem', border: '1px solid #d1d5db', background: 'white' }}
                                                onClick={() => {
                                                    // Open the manual (account-statement) path and require its completion:
                                                    // reset the AA "done" flags so the client must upload the statement.
                                                    setData(p => ({ ...p, aaMethod: 'manual_upload', aaManualUnlocked: true, aaDone: false, aaManualDone: false }));
                                                    setReturnToFinal(true);
                                                    setStep(nextEnabledStep(2, 1));
                                                }}
                                            >
                                                Upload Account Statement Instead
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            <StepReview />
                        </>
                    )}
                    {submitError && (
                        alreadySubmittedError ? (
                            <div className="alp-submitted-lock">
                                <div className="alp-submitted-art" aria-hidden="true">📮</div>
                                <div className="alp-submitted-copy">
                                    <div className="alp-submitted-kicker">Application Status</div>
                                    <h3>Loan Already Submitted</h3>
                                    <p>Your previous loan application is already in review/pending/active state. New application will be available after completion.</p>
                                </div>
                                <div className="alp-submitted-actions">
                                    <button className="alp-btn-ghost" onClick={() => navigate('/dashboard/loans')}>View My Loans</button>
                                    <button className="alp-btn-ghost" onClick={() => navigate('/dashboard')}>Go to Dashboard</button>
                                </div>
                            </div>
                        ) : (
                            <div className="alp-alert alp-aa-error" style={{ marginTop: '1rem' }}>
                                <strong>Error</strong> {submitError}
                            </div>
                        )
                    )}
                    {aaDataPendingError && (
                        <div className="alp-alert" style={{ marginTop: '1rem', background: '#fffbeb', border: '1px solid #fef3c7', padding: '1rem', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                                <span style={{ fontSize: '1.2rem' }}>⏳</span>
                                <div>
                                    <strong style={{ color: '#b45309', display: 'block' }}>Bank Data Pending</strong>
                                    <span style={{ color: '#b45309', fontSize: '0.9rem', opacity: 0.9 }}>
                                        We haven't received your bank data from the Account Aggregator yet. You can retry the Account Aggregator, or upload your bank account statement (last 6 months) instead.
                                    </span>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                <button
                                    className="alp-btn-primary"
                                    style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                                    onClick={() => {
                                        setAaDataPendingError(false);
                                        setStep(nextEnabledStep(2, 1));
                                    }}
                                >
                                    Retry Account Aggregator
                                </button>
                                <button
                                    className="alp-btn-ghost"
                                    style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', border: '1px solid #d1d5db', background: 'white' }}
                                    onClick={() => {
                                        setAaDataPendingError(false);
                                        // Open the manual (account-statement) path and require its completion:
                                        // reset the AA "done" flags so the client must upload the statement.
                                        setData(p => ({ ...p, aaMethod: 'manual_upload', aaManualUnlocked: true, aaDone: false, aaManualDone: false }));
                                        setReturnToFinal(true); // auto-return to Submit once the statement is in
                                        setStep(nextEnabledStep(2, 1));
                                    }}
                                >
                                    Upload Account Statement
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="alp-nav">
                        {step > 0 && (
                            <button className="alp-btn-ghost" onClick={() => setStep(p => nextEnabledStep(p - 1, -1))}>Back</button>
                        )}
                        {!alreadySubmittedError && (
                            <button
                                className="alp-btn-primary"
                                onClick={step === STEPS.length - 1 ? handleFinalSubmit : next}
                                disabled={isSubmitting || aaDataPendingError || (step === 0 && !isEmploymentStepValid) || (step === 1 && !data.kycDone) || (step === 2 && !data.aaDone) || (step === 3 && !data.bankDone) || (step === 4 && !data.selfieDone) || (step === 5 && !data.declarationVideoDone) || (step === 6 && !data.addressProofDone)}
                            >
                                {isSubmitting ? 'Submitting…' : step === STEPS.length - 1 ? 'Submit' : 'Continue'}
                            </button>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
